// ============================================================
// PERRY — THE CINEMATIC STAGE
// The Cinders Road in 3D. Seeded worldgen, a ONE-KNOB light rig,
// a FIXED light count, and a camera that cuts between solved
// marks instead of offering the player a joystick.
//
// LAWS HONORED:
//   • dpr={[1, 1.5]} — the single biggest perf lever there is.
//   • Fixed light count: ambient + hemi + moon + hearth + 2 foe
//     lights + Perry's carried ember (inside PerryBody) = 7,
//     always mounted. Lights are never conditionally rendered;
//     a changing light count recompiles every shader in the scene.
//   • mulberry32 for every hut, pyre and mote. No Math.random in
//     a render path — the road must look the same twice.
//   • Canvas textures cached at module scope and tagged
//     SRGBColorSpace, or the whole palette washes out.
//   • Sky dome: fog=false, depthWrite=false, BackSide, renders
//     first — the horizon must not eat the fog.
//   • The camera reads shotRef in useFrame. Shot changes are
//     CUTS. Within a shot, dollies ride Perry's x on rails.
//   • clientWidth on the parent, never window.innerWidth.
// ============================================================

import { useRef, useMemo, createContext, useContext } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import PerryBody, { BODY_EYE } from "./PerryBody";
import EnemyBody, { billowTexture, handPoint } from "./EnemyBody";
import { Kunai, ChainLinks } from "./FoeWeapons";
import { SD } from "./theme";
import { ROAD_END, GUARD, APPROACH, ENEMIES, SIGHT, hueOf } from "./perryData";
import {
  mulberry32, roseWorld, laneWorld, hookControl, wrapBehind, wrapControl,
  bez, pathOf, skyPoint, bouncePoint, bounceControl, jetMouth,
} from "./qteEngine";
import {
  TEX, NOISE, fbm, normalMapFrom, heightCanvas, fillInstances,
} from "./worldTex";
import FenWorld, { FEN_FOG } from "./FenWorld";

// ── ONE KNOB — turn this if the road is too dark or too hot ──
const LIGHT = { GAIN: 1.12 };

// A tiny context so nested rig pieces can read the director's refs
// without prop-drilling them through the whole scene.
const StageRefs = createContext({});
const useStageRefs = () => useContext(StageRefs);

// ════════════════════════════════════════════════════════════
// THE WORLD — rebuilt 2026-07-27, "THE CINDERS PASS"
// ════════════════════════════════════════════════════════════
// caP'n: "I want the cinder road, the foes, everything in the
// tutorial to look absolutely immaculate — let's see how close
// you can get to Unreal Engine stuff."
//
// You cannot buy that with polygons in a browser. You buy it with
// the four things a real-time renderer actually reads:
//
//   1. SURFACE. Every ground material now carries a painted
//      albedo AND a NORMAL MAP sobel'd out of its own height
//      canvas, plus a roughness map. The moon rakes across it at
//      a low angle and the road suddenly has texture instead of
//      colour. This is the single biggest jump in the file.
//   2. SILHOUETTE + PARALLAX. Layered ridges at three depths,
//      fog-exempt so they survive the fog wall, with mist bands
//      between them. Depth is what makes a frame feel expensive.
//   3. DENSITY, for free. Rubble, reeds, kerb stones, fence posts
//      and stumps are InstancedMesh — thousands of objects, ONE
//      draw call each, seeded so the road is identical twice.
//   4. GRADE. A vignette and a film grain over the canvas, in the
//      DOM, costing zero GPU. Every "cinematic" frame you have
//      ever admired had both.
//
// The light budget did NOT move. Still seven, still fixed, still
// one GAIN knob. Everything that glows here is emissive or
// additive, exactly as the law requires.
// ════════════════════════════════════════════════════════════

// ── WORLDGEN HELPERS — moved out 2026-07-28 ───────────────────
// makeNoise / fbm / NOISE / normalMapFrom / heightCanvas /
// fillInstances / TEX now live in `worldTex.js`, imported above.
// They were always biome-blind; the Drowned Mile needs every one
// of them, and two copies of the same mathematics is how a road
// and a fen end up disagreeing about what a normal map is.

// ── THE ASH FLATS ─────────────────────────────────────────────
function groundTexture() {
  if (TEX.ground) return TEX.ground;
  const c = document.createElement("canvas");
  c.width = 512; c.height = 512;
  const g = c.getContext("2d");
  const R = mulberry32(4242);
  g.fillStyle = "#171210"; g.fillRect(0, 0, 512, 512);
  for (let i = 0; i < 340; i++) {
    g.globalAlpha = 0.05 + R() * 0.1;
    g.fillStyle = R() > 0.5 ? "#312822" : "#090605";
    g.beginPath();
    g.ellipse(R() * 512, R() * 512, 10 + R() * 44, 4 + R() * 15, R() * 3, 0, Math.PI * 2);
    g.fill();
  }
  // drifts of pale ash caught in the low ground
  for (let i = 0; i < 60; i++) {
    g.globalAlpha = 0.05 + R() * 0.09;
    g.fillStyle = "#6a6058";
    g.beginPath();
    g.ellipse(R() * 512, R() * 512, 18 + R() * 60, 6 + R() * 18, R() * 3, 0, Math.PI * 2);
    g.fill();
  }
  // scattered coals that are STILL GOING
  g.globalAlpha = 1;
  for (let i = 0; i < 120; i++) {
    g.fillStyle = R() > 0.6 ? "#d0611f" : "#7a2c10";
    g.globalAlpha = 0.22 + R() * 0.55;
    g.beginPath(); g.arc(R() * 512, R() * 512, 0.9 + R() * 2.6, 0, Math.PI * 2); g.fill();
  }
  g.globalAlpha = 1;
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(22, 9);
  TEX.ground = t;
  return t;
}
function groundNormal() {
  if (TEX.groundN) return TEX.groundN;
  const t = normalMapFrom(heightCanvas(512, 5150, 260, 0.09), 2.6);
  t.repeat.set(22, 9);
  TEX.groundN = t;
  return t;
}

// ── THE ROAD ITSELF ───────────────────────────────────────────
// Packed pale dirt with two wheel tracks worn into it, hoofmarks,
// gravel, and the ash that keeps blowing back over all of it.
function roadTexture() {
  if (TEX.road) return TEX.road;
  const c = document.createElement("canvas");
  c.width = 512; c.height = 512;
  const g = c.getContext("2d");
  const R = mulberry32(60613);
  g.fillStyle = "#3a3128"; g.fillRect(0, 0, 512, 512);
  // gravel
  for (let i = 0; i < 900; i++) {
    g.globalAlpha = 0.1 + R() * 0.35;
    g.fillStyle = R() > 0.6 ? "#5b5044" : "#1d1815";
    g.beginPath(); g.arc(R() * 512, R() * 512, 0.7 + R() * 2.6, 0, Math.PI * 2); g.fill();
  }
  // the two wheel tracks — darker, packed, running the long way
  [150, 362].forEach((cy) => {
    for (let i = 0; i < 260; i++) {
      g.globalAlpha = 0.05 + R() * 0.16;
      g.fillStyle = R() > 0.5 ? "#241d18" : "#12100e";
      const y = cy + (R() - 0.5) * 54;
      g.beginPath();
      g.ellipse(R() * 512, y, 16 + R() * 60, 3 + R() * 8, 0, 0, Math.PI * 2);
      g.fill();
    }
  });
  // dragged marks — something heavy went down this road burning
  for (let i = 0; i < 22; i++) {
    g.globalAlpha = 0.08 + R() * 0.14;
    g.strokeStyle = "#0d0a08";
    g.lineWidth = 3 + R() * 9;
    g.lineCap = "round";
    const y = R() * 512;
    g.beginPath();
    g.moveTo(R() * 200, y);
    g.lineTo(200 + R() * 300, y + (R() - 0.5) * 30);
    g.stroke();
  }
  // ash blown back over it, and the odd live coal
  for (let i = 0; i < 90; i++) {
    g.globalAlpha = 0.05 + R() * 0.1;
    g.fillStyle = "#736758";
    g.beginPath();
    g.ellipse(R() * 512, R() * 512, 10 + R() * 34, 4 + R() * 10, R() * 3, 0, Math.PI * 2);
    g.fill();
  }
  for (let i = 0; i < 60; i++) {
    g.globalAlpha = 0.3 + R() * 0.5;
    g.fillStyle = R() > 0.55 ? "#e0752a" : "#8a3410";
    g.beginPath(); g.arc(R() * 512, R() * 512, 0.8 + R() * 2, 0, Math.PI * 2); g.fill();
  }
  g.globalAlpha = 1;
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(14, 1);
  TEX.road = t;
  return t;
}
function roadNormal() {
  if (TEX.roadN) return TEX.roadN;
  const t = normalMapFrom(heightCanvas(512, 8823, 520, 0.05), 3.1);
  t.repeat.set(14, 1);
  TEX.roadN = t;
  return t;
}

function skyTexture() {
  if (TEX.sky) return TEX.sky;
  const c = document.createElement("canvas");
  c.width = 16; c.height = 256;
  const g = c.getContext("2d");
  const grad = g.createLinearGradient(0, 0, 0, 256);
  grad.addColorStop(0.0, "#07050f");    // cold zenith
  grad.addColorStop(0.3, "#140b1a");
  grad.addColorStop(0.5, "#2a1220");    // bruised night
  grad.addColorStop(0.7, "#54200f");    // smoke catching the fire
  grad.addColorStop(0.86, "#9a3a10");   // the glow of what is left of home
  grad.addColorStop(1.0, "#2a0e06");
  g.fillStyle = grad; g.fillRect(0, 0, 16, 256);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  TEX.sky = t;
  return t;
}

function charWoodTexture() {
  if (TEX.wood) return TEX.wood;
  const c = document.createElement("canvas");
  c.width = 256; c.height = 256;
  const g = c.getContext("2d");
  const R = mulberry32(8181);
  g.fillStyle = "#14100e"; g.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 80; i++) {
    g.globalAlpha = 0.18 + R() * 0.3;
    g.strokeStyle = R() > 0.7 ? "#5a3a22" : "#241a14";
    g.lineWidth = 1 + R() * 3;
    g.beginPath();
    const x = R() * 256;
    g.moveTo(x, 0);
    g.bezierCurveTo(x + (R() - 0.5) * 40, 85, x + (R() - 0.5) * 40, 170, x + (R() - 0.5) * 30, 256);
    g.stroke();
  }
  // alligator-cracked char — the signature of wood that has burned
  for (let i = 0; i < 120; i++) {
    g.globalAlpha = 0.16 + R() * 0.24;
    g.strokeStyle = "#050403";
    g.lineWidth = 1 + R() * 2;
    const x = R() * 256, y = R() * 256, s = 6 + R() * 16;
    g.strokeRect(x, y, s, s * (0.5 + R()));
  }
  for (let i = 0; i < 40; i++) {
    g.globalAlpha = 0.4 + R() * 0.5;
    g.fillStyle = R() > 0.5 ? "#d0611f" : "#7a2c10";
    g.beginPath(); g.arc(R() * 256, R() * 256, 0.8 + R() * 2, 0, Math.PI * 2); g.fill();
  }
  g.globalAlpha = 1;
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  TEX.wood = t;
  return t;
}

// ── SKY DOME — first, fogless, inside-out ────────────────────
function SkyDome() {
  const tex = useMemo(() => skyTexture(), []);
  return (
    <mesh renderOrder={-1000} frustumCulled={false}>
      <sphereGeometry args={[220, 24, 16]} />
      <meshBasicMaterial map={tex} side={THREE.BackSide} fog={false} depthWrite={false} toneMapped={false} />
    </mesh>
  );
}

// ── THE MOON AND THE STARS ───────────────────────────────────
// The moon is the only real shadow caster in the game, so it gets
// to be a thing you can actually see. Halo, disc, and a smear of
// cloud crossing it.
function Nightlights() {
  const tex = useMemo(() => billowTexture(), []);
  const stars = useMemo(() => {
    const R = mulberry32(2718);
    const n = 260;
    const arr = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const a = R() * Math.PI * 2;
      const el = 0.28 + R() * 0.9;
      const r = 190;
      arr[i * 3] = Math.cos(a) * Math.cos(el) * r;
      arr[i * 3 + 1] = Math.sin(el) * r;
      arr[i * 3 + 2] = Math.sin(a) * Math.cos(el) * r;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(arr, 3));
    return g;
  }, []);

  return (
    <group>
      <points geometry={stars} frustumCulled={false}>
        <pointsMaterial size={0.9} color="#cfd8ff" transparent opacity={0.55} fog={false} depthWrite={false} />
      </points>
      <group position={[-96, 74, -110]}>
        <sprite scale={34}>
          <spriteMaterial map={tex} color="#7f92c8" transparent opacity={0.16}
            blending={THREE.AdditiveBlending} fog={false} depthWrite={false} toneMapped={false} />
        </sprite>
        <sprite scale={11}>
          <spriteMaterial map={tex} color="#e8eeff" transparent opacity={0.85}
            blending={THREE.AdditiveBlending} fog={false} depthWrite={false} toneMapped={false} />
        </sprite>
      </group>
    </group>
  );
}

// ── THE ASH FLATS — displaced, normal-mapped, shadow-taking ───
function Terrain() {
  const map = useMemo(() => groundTexture(), []);
  const nrm = useMemo(() => groundNormal(), []);
  const geo = useMemo(() => {
    const W = ROAD_END + 140, D = 96;
    const g = new THREE.PlaneGeometry(W, D, 150, 74);
    const pos = g.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i) + ROAD_END / 2;
      const z = pos.getY(i);   // still a plane: Y is world Z after the rotation
      let h = (fbm(NOISE, x * 0.055, z * 0.055, 4) - 0.5) * 2.6;
      h += (fbm(NOISE, x * 0.42, z * 0.42, 2) - 0.5) * 0.22;
      // the road corridor is dug in and flattened — carts did that
      const d = Math.abs(z);
      const corridor = d < 5.4 ? 1 - Math.min(1, (d / 5.4) ** 2) : 0;
      h = h * (1 - corridor * 0.94) - corridor * 0.1;
      pos.setZ(i, h);
    }
    g.computeVertexNormals();
    return g;
  }, []);

  return (
    <mesh geometry={geo} rotation={[-Math.PI / 2, 0, 0]} position={[ROAD_END / 2, 0, 0]} receiveShadow>
      <meshStandardMaterial
        map={map} normalMap={nrm} normalScale={new THREE.Vector2(1.5, 1.5)}
        roughness={0.98} metalness={0.02} color="#c8c0b8"
      />
    </mesh>
  );
}

// ── THE CINDERS ROAD — cambered, rutted, kerbed ──────────────
// caP'n asked for the roads. This is a real strip of geometry
// with a crown down the middle, two worn wheel ruts either side
// of it, and stones set along both edges. You can see the ruts in
// the moonlight from the dolly marks, which is the entire point.
function Road() {
  const map = useMemo(() => roadTexture(), []);
  const nrm = useMemo(() => roadNormal(), []);
  const geo = useMemo(() => {
    const W = ROAD_END + 120, D = 5.6;
    const g = new THREE.PlaneGeometry(W, D, 260, 22);
    const pos = g.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i) + ROAD_END / 2;
      const z = pos.getY(i);
      const t = Math.min(1, Math.abs(z) / (D / 2));
      let h = 0.06 * (1 - t * t);                       // the crown
      const rut = (o) => Math.exp(-((z - o) * (z - o)) / 0.20) * 0.05;
      h -= rut(1.15) + rut(-1.15);                      // the wheel tracks
      h += (fbm(NOISE, x * 0.9 + 40, z * 0.9, 3) - 0.5) * 0.03;
      pos.setZ(i, h);
    }
    g.computeVertexNormals();
    return g;
  }, []);

  const stones = useMemo(() => {
    const R = mulberry32(19191);
    const out = [];
    for (let i = 0; i < 260; i++) {
      const x = -50 + R() * (ROAD_END + 110);
      const side = R() > 0.5 ? 1 : -1;
      const s = 0.07 + R() * 0.15;
      out.push({
        x, y: 0.02 + s * 0.35, z: side * (2.7 + R() * 0.45),
        rx: R() * 3, ry: R() * 3, rz: R() * 3,
        sx: s * (0.7 + R() * 0.8), sy: s * (0.5 + R() * 0.6), sz: s * (0.7 + R() * 0.8),
      });
    }
    return out;
  }, []);

  return (
    <group>
      <mesh geometry={geo} rotation={[-Math.PI / 2, 0, 0]} position={[ROAD_END / 2, 0.002, 0]} receiveShadow>
        <meshStandardMaterial
          map={map} normalMap={nrm} normalScale={new THREE.Vector2(1.9, 1.9)}
          roughness={0.92} metalness={0.03} color="#cdc4b8"
        />
      </mesh>
      <instancedMesh
        args={[undefined, undefined, stones.length]} frustumCulled={false} castShadow receiveShadow
        ref={(el) => fillInstances(el, stones)}
      >
        <dodecahedronGeometry args={[1, 0]} />
        <meshStandardMaterial color="#4a423a" roughness={0.95} metalness={0.05} />
      </instancedMesh>
    </group>
  );
}

// ── SCATTER — rubble, reeds, stumps. Three draw calls total. ──
// Nothing is placed inside the road corridor, which is why the
// road reads as a road and not as a gap in some bushes.
function Scatter() {
  const wood = useMemo(() => charWoodTexture(), []);
  const sets = useMemo(() => {
    const R = mulberry32(70707);
    const rubble = [], reeds = [], stumps = [];
    const offRoad = () => {
      const side = R() > 0.5 ? 1 : -1;
      return side * (3.4 + R() * 26);
    };
    for (let i = 0; i < 420; i++) {
      const s = 0.06 + R() * 0.26;
      rubble.push({
        x: -30 + R() * (ROAD_END + 80), y: s * 0.4, z: offRoad(),
        rx: R() * 3, ry: R() * 3, rz: R() * 3,
        sx: s, sy: s * (0.4 + R() * 0.7), sz: s * (0.6 + R() * 0.8),
      });
    }
    for (let i = 0; i < 520; i++) {
      const h = 0.3 + R() * 0.75;
      reeds.push({
        x: -30 + R() * (ROAD_END + 80), y: h * 0.5, z: offRoad(),
        rx: (R() - 0.5) * 0.5, ry: R() * 3, rz: (R() - 0.5) * 0.5,
        sx: 0.07 + R() * 0.08, sy: h, sz: 0.07 + R() * 0.08,
      });
    }
    for (let i = 0; i < 90; i++) {
      const h = 0.5 + R() * 1.5;
      stumps.push({
        x: -30 + R() * (ROAD_END + 80), y: h * 0.5, z: offRoad(),
        rx: (R() - 0.5) * 0.34, ry: R() * 3, rz: (R() - 0.5) * 0.34,
        sx: 0.1 + R() * 0.14, sy: h, sz: 0.1 + R() * 0.14,
      });
    }
    return { rubble, reeds, stumps };
  }, []);

  return (
    <group>
      <instancedMesh
        args={[undefined, undefined, sets.rubble.length]} frustumCulled={false} castShadow receiveShadow
        ref={(el) => fillInstances(el, sets.rubble)}
      >
        <icosahedronGeometry args={[1, 0]} />
        <meshStandardMaterial color="#3e352d" roughness={1} metalness={0.02} />
      </instancedMesh>

      {/* burnt reed tufts — the slow water Perry grew up beside,
          and the thing his own Treasure was cut from */}
      <instancedMesh
        args={[undefined, undefined, sets.reeds.length]} frustumCulled={false} castShadow
        ref={(el) => fillInstances(el, sets.reeds)}
      >
        <coneGeometry args={[1, 1, 4, 1, true]} />
        <meshStandardMaterial
          color="#2b2018" roughness={1} side={THREE.DoubleSide}
          emissive="#6a2408" emissiveIntensity={0.16}
        />
      </instancedMesh>

      <instancedMesh
        args={[undefined, undefined, sets.stumps.length]} frustumCulled={false} castShadow receiveShadow
        ref={(el) => fillInstances(el, sets.stumps)}
      >
        <cylinderGeometry args={[0.7, 1, 1, 6]} />
        <meshStandardMaterial map={wood} roughness={1} emissive="#7a2408" emissiveIntensity={0.1} />
      </instancedMesh>
    </group>
  );
}

// ── THE FENCELINE — posts, most of them leaning, one of them
//    with a crow on it ──────────────────────────────────────
function Fenceline() {
  const wood = useMemo(() => charWoodTexture(), []);
  const posts = useMemo(() => {
    const R = mulberry32(4477);
    const out = [];
    for (let i = 0; i < 74; i++) {
      const x = -14 + i * 1.65 + (R() - 0.5) * 0.4;
      const side = -1;                          // one fence, one side of the road
      const h = 0.7 + R() * 0.6;
      if (R() < 0.22) continue;                 // gaps: it is not a tidy fence
      out.push({
        x, y: h * 0.5, z: side * (3.9 + R() * 0.5),
        rx: (R() - 0.5) * 0.5, ry: R() * 3, rz: (R() - 0.5) * 0.45,
        sx: 0.055 + R() * 0.03, sy: h, sz: 0.055 + R() * 0.03,
      });
    }
    return out;
  }, []);
  return (
    <instancedMesh
      args={[undefined, undefined, posts.length]} frustumCulled={false} castShadow receiveShadow
      ref={(el) => fillInstances(el, posts)}
    >
      <cylinderGeometry args={[0.8, 1, 1, 5]} />
      <meshStandardMaterial map={wood} roughness={1} emissive="#8a2c0c" emissiveIntensity={0.08} />
    </instancedMesh>
  );
}

// ── REEDBROOK — the ruins, and the pyres between them ────────
function Ruins() {
  const wood = useMemo(() => charWoodTexture(), []);
  const huts = useMemo(() => {
    const R = mulberry32(90210);
    const out = [];
    for (let i = 0; i < 26; i++) {
      const side = R() > 0.5 ? 1 : -1;
      out.push({
        x: -8 + R() * (ROAD_END + 10),
        z: side * (5.5 + R() * 16),
        w: 1.4 + R() * 2.4,
        h: 1.0 + R() * 2.1,
        rot: (R() - 0.5) * 1.2,
        burnt: R(),
        lean: (R() - 0.5) * 0.35,
      });
    }
    return out;
  }, []);
  const pyres = useMemo(() => {
    const R = mulberry32(31337);
    return Array.from({ length: 22 }, () => ({
      x: -2 + R() * (ROAD_END + 6),
      z: (R() > 0.5 ? 1 : -1) * (3.2 + R() * 10),
      s: 0.5 + R() * 0.95,
      rate: 0.7 + R() * 1.1,
      off: R() * 6.28,
    }));
  }, []);

  const pyreRefs = useRef([]);
  const smokeRefs = useRef([]);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    pyres.forEach((p, i) => {
      const m = pyreRefs.current[i];
      if (m) {
        const f = 0.75 + Math.sin(t * p.rate * 3 + p.off) * 0.18 + Math.sin(t * p.rate * 7.3 + p.off) * 0.07;
        m.scale.set(p.s * (0.9 + f * 0.2), p.s * f * 1.5, p.s * (0.9 + f * 0.2));
        if (m.material) m.material.opacity = 0.42 + f * 0.3;
      }
      const s = smokeRefs.current[i];
      if (s) {
        const life = (t * 0.18 + p.off) % 1;
        s.position.y = p.s * 0.8 + life * 3.4;
        s.scale.setScalar(p.s * (0.7 + life * 2.2));
        if (s.material) s.material.opacity = (1 - life) * 0.22;
      }
    });
  });

  const bill = useMemo(() => billowTexture(), []);

  return (
    <group>
      {huts.map((h, i) => (
        <group key={i} position={[h.x, 0, h.z]} rotation={[0, h.rot, h.lean]}>
          {/* walls — a leaning shell, corners knocked off */}
          <mesh castShadow receiveShadow position={[0, h.h / 2, 0]}>
            <cylinderGeometry args={[h.w * 0.62, h.w * 0.68, h.h, 6, 1, true]} />
            <meshStandardMaterial
              map={wood} roughness={0.95} side={THREE.DoubleSide}
              emissive="#8a2c0c" emissiveIntensity={0.06 + h.burnt * 0.14}
            />
          </mesh>
          {/* what is left of the roof */}
          {h.burnt < 0.62 && (
            <mesh castShadow position={[0, h.h + h.w * 0.24, 0]} rotation={[0, 0.4, 0]}>
              <coneGeometry args={[h.w * 0.85, h.w * 0.7, 6]} />
              <meshStandardMaterial map={wood} roughness={1} />
            </mesh>
          )}
          {/* fallen beams leaning off it, because nothing collapsed neatly */}
          {h.burnt > 0.35 && (
            <mesh castShadow position={[h.w * 0.5, h.h * 0.42, h.w * 0.2]} rotation={[0.4, 0.7, 1.15]}>
              <cylinderGeometry args={[0.06, 0.09, h.h * 1.5, 5]} />
              <meshStandardMaterial map={wood} roughness={1} emissive="#8a2c0c" emissiveIntensity={0.12} />
            </mesh>
          )}
          {/* a window with something still burning behind it */}
          <mesh position={[0, h.h * 0.5, h.w * 0.6]}>
            <planeGeometry args={[h.w * 0.3, h.h * 0.3]} />
            <meshBasicMaterial color="#ff9a3c" toneMapped={false} transparent opacity={0.18 + h.burnt * 0.5} />
          </mesh>
        </group>
      ))}

      {pyres.map((p, i) => (
        <group key={`p${i}`} position={[p.x, 0, p.z]}>
          <mesh position={[0, 0.06, 0]} receiveShadow>
            <cylinderGeometry args={[p.s * 0.5, p.s * 0.62, 0.12, 8]} />
            <meshStandardMaterial color="#0d0a08" roughness={1} emissive="#7a2408" emissiveIntensity={0.5} />
          </mesh>
          <mesh ref={(el) => { pyreRefs.current[i] = el; }} position={[0, 0.42, 0]}>
            <coneGeometry args={[0.28, 0.9, 7]} />
            <meshBasicMaterial color="#ff7a24" transparent opacity={0.55} depthWrite={false} toneMapped={false} />
          </mesh>
          {/* the smoke off it — gaseous, depthWrite off, drifting up */}
          <sprite ref={(el) => { smokeRefs.current[i] = el; }} position={[0, 1, 0]}>
            <spriteMaterial map={bill} color="#4a3c38" transparent opacity={0.2}
              depthWrite={false} toneMapped={false} />
          </sprite>
        </group>
      ))}
    </group>
  );
}

// ── THE BURNING VILLAGE, BEHIND HIM, ALL ACT ─────────────────
// Reedbrook is at his back for the whole road and the frame never
// lets him forget it. Fog-exempt on purpose: it has to still be
// on the horizon at x=44, three minutes later, when he arrives at
// somewhere that isn't on fire.
function Reedbrook() {
  const tex = useMemo(() => billowTexture(), []);
  const fire = useRef([]);
  const smoke = useRef([]);
  const setup = useMemo(() => {
    const R = mulberry32(1848);
    return {
      fire: Array.from({ length: 14 }, () => ({
        x: -34 + R() * 24, y: 1 + R() * 5, z: -12 + R() * 24,
        s: 3 + R() * 7, rate: 0.5 + R() * 0.9, off: R() * 6.28,
      })),
      smoke: Array.from({ length: 16 }, () => ({
        x: -34 + R() * 24, z: -12 + R() * 24,
        s: 7 + R() * 13, rate: 0.05 + R() * 0.05, off: R(),
      })),
    };
  }, []);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    setup.fire.forEach((f, i) => {
      const m = fire.current[i];
      if (!m) return;
      const k = 0.8 + Math.sin(t * f.rate * 2.3 + f.off) * 0.16 + Math.sin(t * f.rate * 6.1 + f.off) * 0.06;
      m.scale.setScalar(f.s * k);
      if (m.material) m.material.opacity = 0.16 + k * 0.16;
    });
    setup.smoke.forEach((s, i) => {
      const m = smoke.current[i];
      if (!m) return;
      const life = (t * s.rate + s.off) % 1;
      m.position.set(s.x - life * 6, 3 + life * 30, s.z - life * 4);
      m.scale.setScalar(s.s * (0.5 + life * 1.9));
      if (m.material) m.material.opacity = (1 - life) * 0.16;
    });
  });

  return (
    <group>
      {setup.fire.map((f, i) => (
        <sprite key={i} ref={(el) => { fire.current[i] = el; }} position={[f.x, f.y, f.z]}>
          <spriteMaterial map={tex} color="#ff7a24" transparent opacity={0.24}
            blending={THREE.AdditiveBlending} fog={false} depthWrite={false} toneMapped={false} />
        </sprite>
      ))}
      {setup.smoke.map((s, i) => (
        <sprite key={`s${i}`} ref={(el) => { smoke.current[i] = el; }}>
          <spriteMaterial map={tex} color="#2a1c18" transparent opacity={0.14}
            fog={false} depthWrite={false} toneMapped={false} />
        </sprite>
      ))}
      {/* the glow it throws on the underside of the sky */}
      <sprite position={[-24, 6, 0]} scale={[70, 26, 1]}>
        <spriteMaterial map={tex} color="#c8480e" transparent opacity={0.3}
          blending={THREE.AdditiveBlending} fog={false} depthWrite={false} toneMapped={false} />
      </sprite>
    </group>
  );
}

// ── THE RIDGES — three depths of silhouette ──────────────────
// Fog-exempt, because the fog wall is at 72 and these live past
// it. They are tinted just off the fog colour so they read as
// layers rather than as a hole in the world. This is the cheapest
// depth in real-time rendering and it has been for thirty years.
function Ridges() {
  const layers = useMemo(() => {
    const build = (seed, radius, height, count, y) => {
      const R = mulberry32(seed);
      const pos = [];
      const step = (Math.PI * 2) / count;
      for (let i = 0; i < count; i++) {
        const a0 = i * step, a1 = (i + 1) * step;
        const h = height * (0.45 + R() * 0.9);
        const x0 = Math.cos(a0) * radius, z0 = Math.sin(a0) * radius;
        const x1 = Math.cos(a1) * radius, z1 = Math.sin(a1) * radius;
        const xm = Math.cos((a0 + a1) / 2) * radius * 1.01;
        const zm = Math.sin((a0 + a1) / 2) * radius * 1.01;
        pos.push(x0, y, z0, x1, y, z1, xm, y + h, zm);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(pos), 3));
      g.computeVertexNormals();
      return g;
    };
    return [
      { geo: build(2201, 78, 12, 46, -1), color: "#20120e" },
      { geo: build(3301, 118, 22, 40, -1), color: "#1b1014" },
      { geo: build(4401, 168, 38, 34, -1), color: "#170e14" },
    ];
  }, []);

  return (
    <group position={[ROAD_END / 2, 0, 0]}>
      {layers.map((l, i) => (
        <mesh key={i} geometry={l.geo} frustumCulled={false} renderOrder={-900 + i}>
          <meshBasicMaterial color={l.color} side={THREE.DoubleSide} fog={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

// ── GROUND MIST — the layer that sells the depth ─────────────
function MistBands() {
  const tex = useMemo(() => billowTexture(), []);
  const refs = useRef([]);
  const bands = useMemo(() => {
    const R = mulberry32(5309);
    return Array.from({ length: 16 }, () => ({
      x: -22 + R() * (ROAD_END + 70),
      y: 0.25 + R() * 0.7,
      z: (R() > 0.5 ? 1 : -1) * (4 + R() * 24),
      s: 7 + R() * 15,
      rate: 0.02 + R() * 0.04,
      off: R() * 6.28,
    }));
  }, []);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    bands.forEach((b, i) => {
      const m = refs.current[i];
      if (!m) return;
      m.position.set(b.x + Math.sin(t * b.rate + b.off) * 3.2, b.y, b.z);
      const k = 0.6 + Math.sin(t * b.rate * 3 + b.off) * 0.18;
      m.scale.set(b.s * k, b.s * k * 0.24, 1);
      if (m.material) m.material.opacity = 0.05 + k * 0.05;
    });
  });
  return (
    <group>
      {bands.map((b, i) => (
        <sprite key={i} ref={(el) => { refs.current[i] = el; }}>
          <spriteMaterial map={tex} color="#5a4438" transparent opacity={0.07}
            depthWrite={false} toneMapped={false} />
        </sprite>
      ))}
    </group>
  );
}

// ── THE FENCEPOST CROW — J'ynxi's threadmark ─────────────────
// She is not in this game. She is watching this game.
// `perch` added 2026-07-28: the post she sits on is a fencepost at
// Reedbrook and a mooring post in the fen, and a mooring post
// stands in water and is therefore shorter above the line. One
// number, two levels, one crow.
function Crow({ x = 15, z = -3.2, perch = 1 }) {
  const head = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (head.current) head.current.rotation.y = Math.sin(t * 0.5) * 0.5 + Math.sin(t * 3.1) * 0.05;
  });
  return (
    <group position={[x, 0, z]}>
      <mesh castShadow position={[0, 0.55 * perch, 0]}>
        <cylinderGeometry args={[0.06, 0.08, 1.1 * perch, 6]} />
        <meshStandardMaterial color="#241a12" roughness={1} />
      </mesh>
      <group position={[0, 1.22 * perch, 0]}>
        <mesh castShadow scale={[0.7, 0.85, 1.1]}>
          <sphereGeometry args={[0.14, 12, 10]} />
          <meshStandardMaterial color="#0d0b10" roughness={0.7} />
        </mesh>
        <group ref={head} position={[0, 0.14, 0.05]}>
          <mesh castShadow scale={[0.8, 0.8, 0.9]}>
            <sphereGeometry args={[0.085, 10, 8]} />
            <meshStandardMaterial color="#0d0b10" roughness={0.7} />
          </mesh>
          <mesh position={[0, 0, 0.1]} rotation={[Math.PI / 2, 0, 0]}>
            <coneGeometry args={[0.03, 0.11, 6]} />
            <meshStandardMaterial color="#3a3020" roughness={0.6} />
          </mesh>
          {/* the knowing eye — teal, because of course it is */}
          <mesh position={[0.055, 0.02, 0.06]}>
            <circleGeometry args={[0.016, 8]} />
            <meshBasicMaterial color={SD.tealLight} toneMapped={false} />
          </mesh>
        </group>
        {/* tail */}
        <mesh position={[0, -0.02, -0.16]} rotation={[0.4, 0, 0]} scale={[0.5, 0.2, 1.4]}>
          <sphereGeometry args={[0.1, 8, 6]} />
          <meshStandardMaterial color="#0d0b10" roughness={0.8} />
        </mesh>
      </group>
    </group>
  );
}

// ── HOLLOWMERE — lantern light at the end of the road ────────
function LanternGate() {
  const glow = useRef();
  const lamps = useRef([]);
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (glow.current?.material) glow.current.material.opacity = 0.42 + Math.sin(t * 1.4) * 0.1;
    lamps.current.forEach((m, i) => {
      if (m?.material) m.material.opacity = 0.75 + Math.sin(t * 2.3 + i * 1.7) * 0.2;
    });
  });
  const tex = useMemo(() => billowTexture(), []);
  return (
    <group position={[ROAD_END + 5, 0, 0]}>
      {[-2.6, 2.6].map((z, i) => (
        <group key={i} position={[0, 0, z]}>
          <mesh castShadow position={[0, 1.4, 0]}>
            <cylinderGeometry args={[0.12, 0.18, 2.8, 8]} />
            <meshStandardMaterial color="#2a1e14" roughness={1} />
          </mesh>
          {/* an iron cage, and a light inside it */}
          <mesh position={[0, 2.85, 0]}>
            <octahedronGeometry args={[0.24, 0]} />
            <meshStandardMaterial color="#4a4038" metalness={0.7} roughness={0.5}
              emissive="#ffb058" emissiveIntensity={0.5} />
          </mesh>
          <sprite ref={(el) => { lamps.current[i] = el; }} position={[0, 2.85, 0]} scale={1.4}>
            <spriteMaterial map={tex} color="#ffd48a" transparent opacity={0.8}
              blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
          </sprite>
        </group>
      ))}
      {/* the warm wall of light past the ridge — home, or the next
          best thing to it */}
      <sprite ref={glow} position={[4, 2, 0]} scale={[14, 8, 1]}>
        <spriteMaterial map={tex} color="#ffb058" transparent opacity={0.45}
          blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
      </sprite>
    </group>
  );
}

// ── ASH AND EMBERS — two point fields, two draw calls ────────
function AshMotes({ count = 520 }) {
  const geo = useMemo(() => {
    const R = mulberry32(777);
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = -8 + R() * (ROAD_END + 24);
      arr[i * 3 + 1] = 0.1 + R() * 7;
      arr[i * 3 + 2] = -22 + R() * 44;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(arr, 3));
    return g;
  }, [count]);
  const pts = useRef();
  useFrame((state, dt) => {
    if (!pts.current) return;
    const p = pts.current.geometry.attributes.position;
    const a = p.array;
    for (let i = 1; i < a.length; i += 3) {
      a[i] += dt * 0.22;
      if (a[i] > 7.4) a[i] = 0.05;
    }
    p.needsUpdate = true;
    pts.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.05) * 0.02;
  });
  return (
    <points ref={pts} geometry={geo} frustumCulled={false}>
      <pointsMaterial size={0.055} color="#e8c9a0" transparent opacity={0.45} sizeAttenuation depthWrite={false} />
    </points>
  );
}

// live embers — additive, rising faster, and they die out
function Embers({ count = 240 }) {
  const geo = useMemo(() => {
    const R = mulberry32(3141);
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = -14 + R() * (ROAD_END + 34);
      arr[i * 3 + 1] = R() * 5;
      arr[i * 3 + 2] = -18 + R() * 36;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(arr, 3));
    return g;
  }, [count]);
  const pts = useRef();
  useFrame((state, dt) => {
    if (!pts.current) return;
    const p = pts.current.geometry.attributes.position;
    const a = p.array;
    const t = state.clock.elapsedTime;
    for (let i = 0; i < a.length; i += 3) {
      a[i + 1] += dt * (0.7 + (i % 7) * 0.08);
      a[i] += Math.sin(t * 0.5 + i) * dt * 0.22;
      if (a[i + 1] > 6.5) a[i + 1] = 0.03;
    }
    p.needsUpdate = true;
  });
  return (
    <points ref={pts} geometry={geo} frustumCulled={false}>
      <pointsMaterial
        size={0.075} color="#ff9a3c" transparent opacity={0.75} sizeAttenuation
        depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false}
      />
    </points>
  );
}


// ════════════════════════════════════════════════════════════
// THE FIGHT LAYER
// Everything below exists because the HUD no longer tells you
// anything. The strike's angle, its timing and its outcome are
// all communicated by objects that live IN THE WORLD, made of
// the same fire the foe is made of. Not one of them re-renders:
// they read the director's refs inside useFrame and write
// transforms and material opacities imperatively.
//
// AXES, stated once so nothing ever guesses again — REWRITTEN
// 2026-07-27 after the rose was found to be unreadable in play.
//
//   The old note here claimed ax → world X and ay → world Y, and
//   that claim was the bug. On every fight mark, world X is the
//   ROAD — the same axis the enemy already stands on. So all six
//   angles resolved onto one line pointing straight at the foe,
//   every telegraph flew in along that same line, and six
//   distinct answers rendered as one indistinguishable picture.
//   You could not tell S from F because there was nothing to see.
//
//   THE LAW NOW: ax and ay are SCREEN offsets, resolved against
//   the live camera's right/up vectors by `roseWorld` in
//   qteEngine. S is up-left ON THE GLASS. F is up-right ON THE
//   GLASS. Always, under every shot, at every point on the road.
//
//   And a strike may only ARRIVE from the place its key names —
//   the foe walks to that lane (`step`), or the fire loops around
//   to it (`hook`), or it comes down the vertical (`fall`). No
//   attack is ever allowed to be delivered from the wrong side of
//   the frame, because that is a lie told to the player's hands.
// ════════════════════════════════════════════════════════════

// Which foe is the one actually swinging.
function actorIndex(stateArr) {
  const A = stateArr?.current;
  if (!A) return 0;
  for (let i = 0; i < 2; i++) if (A[i]?.actor && A[i]?.phase !== "hidden") return i;
  for (let i = 0; i < 2; i++) {
    const ph = A[i]?.phase;
    if (ph === "wind" || ph === "strike" || ph === "cast" || ph === "hurl" || ph === "feint") return i;
  }
  return 0;
}

// ── LANE DRIVER — the feet ───────────────────────────────────
// caP'n's ruling made flesh: "there should either be an enemy
// there on my left, or the top, or they can be on my right throw
// a hook bring it back so it comes to my left."
//
// This is the FIRST of those. During a `step` attack the foe
// physically crosses the frame to the side the blow is going to
// land on, and it does it during the readable first half of the
// wind-up. You do not decode a symbol; you watch a thing walk to
// where it is about to hit you from. Nothing else in this build
// buys as much readability per line of code.
//
// It writes the DIRECTOR'S foePos ref. That is deliberate and it
// is the one place the stage is allowed to: a lane is a fact
// about the camera, and the camera is not in the director.
function LaneDriver({ posRef, foePos, foeState, strikeRef }) {
  const { camera } = useThree();
  const want = useRef(new THREE.Vector3());

  useFrame((_, dt) => {
    const p = posRef.current;
    if (!p) return;
    const st = strikeRef?.current;
    const act = actorIndex(foeState);

    for (let i = 0; i < 2; i++) {
      const fs = foeState.current[i];
      const fp = foePos.current[i];
      if (!fs || !fp || fs.phase === "hidden") continue;

      const isActor = i === act;
      const planting = isActor && fs.path === "wrap";
      const stepping = isActor && st && !planting && (fs.path === "step" || !fs.path);

      // ── THE PLANT ──
      // A `wrap` attack does not take a lane. It stands square in
      // front of you, upstage, and throws past your ear — caP'n's
      // spec verbatim, and the reason the chain reads at all: if
      // the thrower drifted, "behind you" would drift with it.
      if (planting) {
        const m = camera.matrixWorld.elements;
        const sx = fs.lane * APPROACH.PLANT_X;
        const up = APPROACH.PLANT_Z;
        const wx = p.x + m[0] * sx - m[8] * up;
        const wz = p.z + m[2] * sx - m[10] * up;
        const k = Math.min(1, dt * 2.6);
        let dx = (wx - fp.x) * k, dz = (wz - fp.z) * k;
        const cap = APPROACH.SPEED_CAP * dt;
        const d0 = Math.hypot(dx, dz);
        if (d0 > cap) { const r = cap / d0; dx *= r; dz *= r; }
        fp.x += dx; fp.z += dz;
        continue;
      }
      // A stepping foe goes to the lane its blow comes from. Every
      // other foe holds its own lane, further upstage, out of the
      // way of the answer.
      const key = stepping
        ? (st.angle || fs.angle || "F")
        : (fs.lane < 0 ? "D" : "K");   // an idle foe holds its own column, mid row
      laneWorld(camera, p, key, want.current);
      if (!stepping) {
        // idle foes stand a half pace further out so the fighting
        // lane always reads as the closer, hotter one
        want.current.x += (want.current.x - p.x) * 0.30;
        want.current.z += (want.current.z - p.z) * 0.30;
      }

      // approach is FAST (it is scrambling) and the settle is slow
      const prog = fs.prog || 0;
      const hurrying = stepping && prog < APPROACH.FRAC;
      const k = Math.min(1, dt * (hurrying ? 7.0 : 2.0));
      let dx = (want.current.x - fp.x) * k;
      let dz = (want.current.z - fp.z) * k;
      // nothing in this game teleports
      const cap = APPROACH.SPEED_CAP * dt;
      const d = Math.hypot(dx, dz);
      if (d > cap) { const r = cap / d; dx *= r; dz *= r; }
      fp.x += dx;
      fp.z += dz;
    }
  });
  return null;
}

// ── THE ARC — the incoming line of fire ──────────────────────
// A tapered ember streak that GROWS from wherever the blow is
// actually coming from, to the point on the rose it will arrive
// at. Its length is the timer; when the head touches him, that
// is impact. It is a piece of burning debris rather than a
// widget, and it is now drawn on a CURVE, because a hook that
// travels in a straight line is not a hook.
//
// Built from a fixed pool of 14 segments. Fixed count, no
// allocation, no mount/unmount — a bezier drawn with beads.
const ARC_SEGS = 14;

// `half` added 2026-07-28 with the chord. Half 0 is every strike
// there has ever been and draws `st.angle`. Half 1 exists ONLY
// while a chord is live and draws `st.angle2` — it hides itself
// entirely the rest of the time, so the cost of the second arc on
// every non-chord strike in the game is one boolean.
function StrikeArc({ strikeRef, posRef, foePos, foeState, half = 0 }) {
  const { camera } = useThree();
  const segs = useRef([]);
  const bloom = useRef();
  const ghost = useRef([]);
  const tex = useMemo(() => billowTexture(), []);
  const a = useRef(new THREE.Vector3());
  const c = useRef(new THREE.Vector3());
  const b = useRef(new THREE.Vector3());
  const pt = useRef(new THREE.Vector3());
  const nx = useRef(new THREE.Vector3());

  useFrame(() => {
    const st0 = strikeRef?.current;
    const bl = bloom.current;
    if (!segs.current[0] || !bl) return;

    // the second arc exists only for the second half of a chord
    const st = (half === 1)
      ? (st0 && st0.angle2 ? { ...st0, angle: st0.angle2 } : null)
      : st0;

    if (!st) {
      segs.current.forEach((m) => { if (m) m.visible = false; });
      ghost.current.forEach((m) => { if (m) m.visible = false; });
      bl.visible = false;
      return;
    }

    const p = posRef.current;
    const i = actorIndex(foeState);
    const fp = foePos.current[i] || { x: p.x + 1.2, y: 0, z: p.z };
    const path = pathOf(st);

    // ── THE WRAP DRAWS ITSELF ──
    // A Chain Kunai's telegraph is a real object on a real chain,
    // rendered by `ChainKunai` below. If the bead-arc also painted
    // it we would be showing the player two different lies about
    // the same blow. The arc stands down.
    if (path === "wrap") {
      segs.current.forEach((m) => { if (m) m.visible = false; });
      ghost.current.forEach((m) => { if (m) m.visible = false; });
      bl.visible = false;
      return;
    }

    const d = GUARD.DIRS[st.angle] || GUARD.DIRS.D;

    const t = performance.now();
    const span = st.impactAt - st.spawnAt;
    const prog = span > 0 ? Math.max(0, Math.min(1.1, (t - st.spawnAt) / span)) : 1;

    // ── WHERE IT LANDS — the rose point, on the camera plane ──
    roseWorld(camera, p, st.angle, b.current);

    // ── WHERE IT COMES FROM — and this is the whole fix ──
    if (path === "fall") {
      // ROW-DRIVEN as of 2026-07-27. `fall` used to be a boolean —
      // overhead dropped, everything else skimmed the dirt. With a
      // three-row grid it means "arrive from the height your row
      // names": the top row drops out of the dark, the bottom row
      // comes up off the ground, and the middle row comes in FLAT.
      // That last case is why the Lunge needed no rewrite at all.
      if (d.tier === "top") {
        a.current.set(b.current.x, (p.y || 0) + 2.6, b.current.z);
        c.current.set(b.current.x, (p.y || 0) + 1.7, b.current.z);
      } else if (d.tier === "low") {
        a.current.set(fp.x, 0.06, fp.z);
        c.current.copy(a.current).lerp(b.current, 0.5);
        c.current.y = 0.05;
      } else {
        // level — it comes straight in at the height it will land,
        // sagging only slightly so the bezier still reads as travel
        a.current.set(fp.x, b.current.y, fp.z);
        c.current.copy(a.current).lerp(b.current, 0.5);
        c.current.y = b.current.y - 0.10;
      }
    } else if (path === "hook") {
      // caP'n's boomerang: it leaves the foe, swings outside the
      // frame past the target, and comes back in pointing the
      // right way. The tangent at the end IS the arrival angle.
      a.current.set(fp.x, 0.78, fp.z);
      hookControl(camera, p, st.angle, c.current);
    } else {
      // `step` — the foe is standing in the lane already, so a
      // straight reach from its lit limb is already honest.
      a.current.set(fp.x, 0.62 + d.ay * 0.34, fp.z);
      c.current.copy(a.current).lerp(b.current, 0.5);
      c.current.y += 0.22;
    }

    // a lie never finishes the reach
    const grow = st.feint ? Math.min(0.52, prog * 0.78) : Math.min(1, prog);

    // ── draw the curve as beads, tapering toward the head ──
    for (let k = 0; k < ARC_SEGS; k++) {
      const m = segs.current[k];
      if (!m) continue;
      const u = (k + 1) / ARC_SEGS;
      if (u > grow) { m.visible = false; continue; }
      m.visible = true;
      bez(a.current, c.current, b.current, u, pt.current);
      m.position.copy(pt.current);
      // fat at the head, thin at the tail — it reads as travelling
      const head = u / Math.max(0.001, grow);
      m.scale.setScalar((0.055 + head * 0.16) * (0.7 + prog * 0.5));
      const mat = m.material;
      if (mat) {
        mat.color.set(st.tell);
        mat.opacity = st.feint
          ? (0.18 + Math.sin(t * 0.012) * 0.10) * head
          : (0.22 + prog * 0.5) * (0.35 + head * 0.75);
      }
    }

    // ── the bloom on the rose point — it TIGHTENS on arrival ──
    bl.visible = !st.feint;
    bl.position.copy(b.current);
    const tight = 2.0 - Math.min(1, prog) * 1.3;
    bl.scale.setScalar(Math.max(0.24, tight * 0.44));
    const bm = bl.material;
    if (bm) { bm.color.set(st.tell); bm.opacity = 0.20 + Math.min(1, prog) * 0.70; }

    // ── Third Eye — the WHOLE curve, shown up front ──
    ghost.current.forEach((m, k) => {
      if (!m) return;
      if (!st.foreseen) { m.visible = false; return; }
      m.visible = true;
      bez(a.current, c.current, b.current, (k + 1) / ARC_SEGS, nx.current);
      m.position.copy(nx.current);
      m.scale.setScalar(0.05);
      if (m.material) m.material.opacity = 0.20 + Math.sin(t * 0.006 + k) * 0.06;
    });
  });

  return (
    <group>
      {Array.from({ length: ARC_SEGS }, (_, k) => (
        <sprite key={k} ref={(el) => { segs.current[k] = el; }} visible={false}>
          <spriteMaterial
            map={tex} color="#ffb058" transparent opacity={0} depthWrite={false}
            blending={THREE.AdditiveBlending} toneMapped={false}
          />
        </sprite>
      ))}
      {Array.from({ length: ARC_SEGS }, (_, k) => (
        <sprite key={`g${k}`} ref={(el) => { ghost.current[k] = el; }} visible={false}>
          <spriteMaterial
            map={tex} color="#4ff0dc" transparent opacity={0} depthWrite={false}
            blending={THREE.AdditiveBlending} toneMapped={false}
          />
        </sprite>
      ))}
      <sprite ref={bloom} scale={0.6}>
        <spriteMaterial
          map={tex} transparent depthWrite={false} opacity={0}
          blending={THREE.AdditiveBlending} toneMapped={false}
        />
      </sprite>
    </group>
  );
}

// ── THE TRAINING ROSE — six coals, and one of them is lit ────
// Hints are ON until Hollowmere is reached for the first time and
// OFF forever after (caP'n's call, 2026-07-27). They are not a
// HUD: they are six faint coals hanging in the world exactly
// where the six answers live, so what you learn during the
// tutorial is the SHAPE, in the same space you will keep fighting
// in once they are gone.
//
// LETTERED, 2026-07-27 (caP'n). Two things were true at once: the
// coals never said WHICH key they were, and they were rendering as
// flat white squares — because a `spriteMaterial` with no `map` is
// an untextured quad, and three.js draws that as exactly what it
// is. They were always meant to be coals; they were never given
// anything to be coals WITH. So the letter and the fire arrive in
// the same fix, painted into the texture that was missing.
//
// Two sprites per point, because the borders have to be able to
// breathe independently of the letter:
//   TILE   the key itself, in a double-ruled ember frame
//   FRAME  an outer double ring with flame licking off it, which
//          flickers and shimmers on its own noise
// Four rules of border in total, twice doubled, all of it on fire.

const roundPath = (g, x, y, w, h, r) => {
  g.beginPath();
  g.moveTo(x + r, y);
  g.lineTo(x + w - r, y);       g.quadraticCurveTo(x + w, y, x + w, y + r);
  g.lineTo(x + w, y + h - r);   g.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  g.lineTo(x + r, y + h);       g.quadraticCurveTo(x, y + h, x, y + h - r);
  g.lineTo(x, y + r);           g.quadraticCurveTo(x, y, x + r, y);
  g.closePath();
};

// The one fire ramp both textures are ruled with. Cold coals are
// this same ramp multiplied down by material.color, so a lit coal
// and a dead one are the same object at two temperatures.
const emberGrad = (g) => {
  const grad = g.createLinearGradient(0, 0, 0, 256);
  grad.addColorStop(0.00, "#fff0cc");
  grad.addColorStop(0.30, "#ffab3c");
  grad.addColorStop(0.68, "#ff6a1e");
  grad.addColorStop(1.00, "#b62a12");
  return grad;
};

// ── the key tile — one cached 256² canvas per letter ─────────
const KEY_TEX = new Map();
function keyTileTexture(letter) {
  if (KEY_TEX.has(letter)) return KEY_TEX.get(letter);
  const c = document.createElement("canvas");
  c.width = 256; c.height = 256;
  const g = c.getContext("2d");
  const fire = emberGrad(g);

  // the coal glowing behind the letter
  const rg = g.createRadialGradient(128, 128, 6, 128, 128, 122);
  rg.addColorStop(0.00, "rgba(255,196,120,0.50)");
  rg.addColorStop(0.55, "rgba(255,118,40,0.16)");
  rg.addColorStop(1.00, "rgba(0,0,0,0)");
  g.fillStyle = rg; g.fillRect(0, 0, 256, 256);

  // BORDER ONE — the heavy ember rule
  g.strokeStyle = fire; g.lineWidth = 9; g.lineJoin = "round";
  roundPath(g, 30, 30, 196, 196, 16); g.stroke();
  // BORDER TWO — a hot hairline just inside it
  g.strokeStyle = "rgba(255,242,214,0.92)"; g.lineWidth = 2.5;
  roundPath(g, 44, 44, 168, 168, 12); g.stroke();

  // the key. System sans only — a webfont that hasn't loaded yet
  // would be baked into the cache as its fallback, permanently.
  g.textAlign = "center"; g.textBaseline = "middle";
  g.font = "900 132px Verdana, Geneva, sans-serif";
  g.shadowColor = "#ff8a2a"; g.shadowBlur = 30;
  g.fillStyle = "#fff6e4";
  g.fillText(letter, 128, 134);
  g.shadowBlur = 0;
  g.fillText(letter, 128, 134);      // a crisp pass over the glow

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  KEY_TEX.set(letter, t);
  return t;
}

// ── THE BUBBLE — the same coal with nothing written on it ────
// What the rose becomes once the road has been walked. One cached
// canvas for all six; the HUE arrives via material.color, which is
// why this is painted in NEUTRAL WHITE and not in fire. (That is
// the same trap `billowTexture` fell into on 2026-07-27: it baked
// an orange ramp into itself, so every consumer that set a colour
// got that colour multiplied by orange, and nothing in the fight
// layer ever quite matched its tell. Not twice.)
let BUBBLE_TEX = null;
function bubbleTexture() {
  if (BUBBLE_TEX) return BUBBLE_TEX;
  const c = document.createElement("canvas");
  c.width = 256; c.height = 256;
  const g = c.getContext("2d");

  // the coal itself — hot core, soft falloff, no glyph
  const rg = g.createRadialGradient(128, 128, 4, 128, 128, 116);
  rg.addColorStop(0.00, "rgba(255,255,255,0.98)");
  rg.addColorStop(0.26, "rgba(255,255,255,0.62)");
  rg.addColorStop(0.62, "rgba(255,255,255,0.20)");
  rg.addColorStop(1.00, "rgba(255,255,255,0)");
  g.fillStyle = rg; g.fillRect(0, 0, 256, 256);

  // BORDER ONE — a ring, so a bubble still reads as a MADE thing
  // and not as bloom. The letter used to do that job.
  g.strokeStyle = "rgba(255,255,255,0.92)"; g.lineWidth = 7;
  g.beginPath(); g.arc(128, 128, 74, 0, Math.PI * 2); g.stroke();
  // BORDER TWO — a hairline standing off it
  g.strokeStyle = "rgba(255,255,255,0.55)"; g.lineWidth = 2.5;
  g.beginPath(); g.arc(128, 128, 90, 0, Math.PI * 2); g.stroke();

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  BUBBLE_TEX = t;
  return t;
}

// ── the outer fire frame — one cached canvas, shared by all six ──
let FRAME_TEX = null;
function fireFrameTexture() {
  if (FRAME_TEX) return FRAME_TEX;
  const c = document.createElement("canvas");
  c.width = 256; c.height = 256;
  const g = c.getContext("2d");
  const fire = emberGrad(g);
  const R = mulberry32(6612);        // DETERMINISM LAW — the road burns the same twice

  // flame licking outward off the rule, seeded so it never shimmers
  // differently between two runs of the same Solrend
  for (let i = 0; i < 52; i++) {
    const side = i % 4;
    const along = 26 + R() * 204;
    const len = 12 + R() * 26;
    let x, y, dx, dy;
    if (side === 0) { x = along; y = 20; dx = 0; dy = -1; }
    else if (side === 1) { x = along; y = 236; dx = 0; dy = 1; }
    else if (side === 2) { x = 20; y = along; dx = -1; dy = 0; }
    else { x = 236; y = along; dx = 1; dy = 0; }
    const lg = g.createLinearGradient(x, y, x + dx * len, y + dy * len);
    lg.addColorStop(0, "rgba(255,150,60,0.75)");
    lg.addColorStop(1, "rgba(255,60,20,0)");
    g.strokeStyle = lg;
    g.lineWidth = 2 + R() * 4;
    g.lineCap = "round";
    g.beginPath();
    g.moveTo(x, y);
    g.quadraticCurveTo(x + dx * len * 0.6 + (R() - 0.5) * 9,
                       y + dy * len * 0.6 + (R() - 0.5) * 9,
                       x + dx * len, y + dy * len);
    g.stroke();
  }

  // BORDER THREE — the outer ember rule
  g.strokeStyle = fire; g.lineWidth = 10; g.lineJoin = "round";
  roundPath(g, 24, 24, 208, 208, 20); g.stroke();
  // BORDER FOUR — a thin ring standing off it
  g.strokeStyle = "rgba(255,176,88,0.80)"; g.lineWidth = 3.5;
  roundPath(g, 8, 8, 240, 240, 26); g.stroke();

  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  FRAME_TEX = t;
  return t;
}

// ── SIZES — dials. Verified against the closest on-screen pair
//    (S/J at 179 px ≈ 0.22 world units on s_qte_close): a lit
//    frame at 0.235 beside a dead one at 0.145 clears by 0.03,
//    so a hot coal never overlaps its neighbour.
const ROSE = {
  TILE_COLD: 0.105, TILE_HOT: 0.165,
  FRAME_COLD: 0.145, FRAME_HOT: 0.235,
  DIM: "#9a8270",         // retained: the old neutral, now unused
  COLD_MIX: 0.55,         // how far an unlit coal fades toward black
};

// Blend a hex toward black. An unlit coal keeps its HUE — that is
// the whole point of the colours — but must not compete with the
// one that is lit, so it loses value rather than saturation.
const dimHue = (hex, k) => {
  const h = hex.replace("#", "");
  const r = Math.round(parseInt(h.slice(0, 2), 16) * k);
  const g = Math.round(parseInt(h.slice(2, 4), 16) * k);
  const b = Math.round(parseInt(h.slice(4, 6), 16) * k);
  return (r << 16) | (g << 8) | b;
};
const HUE_HOT = {};
const HUE_COLD = {};
for (const k of GUARD.KEYS) {
  HUE_HOT[k] = new THREE.Color(hueOf(k));
  HUE_COLD[k] = new THREE.Color(dimHue(hueOf(k), ROSE.COLD_MIX));
}

function HintRose({ posRef, strikeRef, shotRef, sightRef }) {
  const { camera } = useThree();
  const tiles = useRef([]);
  const frames = useRef([]);
  const at = useRef(new THREE.Vector3());
  const keys = GUARD.KEYS;
  const frameTex = useMemo(() => fireFrameTexture(), []);
  const bubbleTex = useMemo(() => bubbleTexture(), []);
  const tileTex = useMemo(() => keys.map((k) => keyTileTexture(k)), [keys]);

  useFrame((state) => {
    const p = posRef.current;
    const s = shotRef?.current;
    const st = strikeRef?.current;
    const sg = sightRef?.current;

    // ── WHAT THE COALS ARE ALLOWED TO SAY, THIS FRAME ──
    // The base tier comes from the director; the live charge is
    // overlaid HERE rather than baked into the ref, so it expires
    // exactly when the strike does and there is no latch to forget
    // to un-set. (See syncSight in RunDirector for why.)
    const named = !!(st && st.named);
    const base = sg?.tier || SIGHT.LETTERED;
    const tier = named ? SIGHT.LETTERED : base;
    // Buying the truth buys ALL of the truth: a charge un-moves the
    // coals as well as naming them.
    const shuffle = (tier === SIGHT.SHUFFLED) ? (sg?.shuffle || null) : null;
    // ── PARTIAL ── half the coals lit, half out, and WHICH half
    // is a Set rolled off the run seed at encounter start. A
    // charge buys the whole rose back, same as it un-moves a
    // shuffle: buying the truth buys all of the truth.
    const lit = (tier === SIGHT.PARTIAL) ? (sg?.lit || null) : null;
    // ── THE FERRYMAN'S DUE ── a strike the foe darkened, one at a
    // time, inside an otherwise ordinary fight.
    const dued = !!(st && st.dark && !named);
    const live = !!(p && s?.combat && tier !== SIGHT.DARK && !dued);
    const t = state.clock.elapsedTime;

    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      const tile = tiles.current[i];
      const frame = frames.current[i];
      if (!tile || !frame) continue;
      const out = !live || (lit && !lit.has(k));
      if (out) { tile.visible = false; frame.visible = false; continue; }
      tile.visible = true; frame.visible = true;

      // A coal hangs at its OWN angle, unless something moved it.
      // The fire's real angle is never touched by any of this —
      // every tier is survivable by reading the creature, which is
      // the skill this whole ladder is quietly training.
      roseWorld(camera, p, (shuffle && shuffle[k]) || k, at.current);
      tile.position.copy(at.current);
      frame.position.copy(at.current);

      // LETTER OR BUBBLE. Both textures are cached and neutral, so
      // swapping the map costs nothing and the hue comes from the
      // material either way.
      const wantMap = (tier === SIGHT.LETTERED) ? tileTex[i] : bubbleTex;

      // A chord lights TWO. It has to: the answer is two, and a
      // rose that showed one of them would be telling half a lie
      // in the one place the game has promised never to.
      const hot = !!(st && !st.feint && (st.angle === k || st.angle2 === k));
      // two rates, offset per coal — fire, not a pulse
      const flick = 0.86 + Math.sin(t * 12.3 + i * 2.1) * 0.09
                         + Math.sin(t * 29.7 + i * 5.3) * 0.05;

      const tm = tile.material;
      if (tm) {
        if (tm.map !== wantMap) { tm.map = wantMap; tm.needsUpdate = true; }
        const wantO = hot ? 1 : 0.34;
        tm.opacity += (wantO - tm.opacity) * 0.18;
        // THE HUE IS THE KEY'S, NEVER THE ATTACK'S. This reverses
        // the 2026-07-27 note that had a lit coal take the strike's
        // own `tell`, and the reversal is deliberate: colour has to
        // mean POSITION or the shuffle has nothing left to lie
        // about. The tell still colours the arc, the orb and the
        // sparks — everything that is genuinely about the fire.
        tm.color.copy(hot ? HUE_HOT[k] : HUE_COLD[k]);
      }
      const wantT = hot ? ROSE.TILE_HOT : ROSE.TILE_COLD;
      tile.scale.setScalar(tile.scale.x + (wantT - tile.scale.x) * 0.18);

      const fm = frame.material;
      if (fm) {
        const wantO = hot ? 0.95 * flick : 0.17;
        fm.opacity += (wantO - fm.opacity) * 0.2;
        fm.color.copy(hot ? HUE_HOT[k] : HUE_COLD[k]);
        fm.rotation = Math.sin(t * 1.7 + i) * 0.03;   // heat shimmer, not spin
      }
      const wantF = (hot ? ROSE.FRAME_HOT * flick : ROSE.FRAME_COLD);
      frame.scale.setScalar(frame.scale.x + (wantF - frame.scale.x) * 0.22);
    }
  });

  return (
    <group>
      {keys.map((k, i) => (
        <sprite key={`f${k}`} ref={(el) => { frames.current[i] = el; }}
                visible={false} scale={ROSE.FRAME_COLD}>
          <spriteMaterial
            map={frameTex} transparent opacity={0} depthWrite={false}
            blending={THREE.AdditiveBlending} toneMapped={false}
          />
        </sprite>
      ))}
      {keys.map((k, i) => (
        <sprite key={`t${k}`} ref={(el) => { tiles.current[i] = el; }}
                visible={false} scale={ROSE.TILE_COLD}>
          <spriteMaterial
            map={tileTex[i]} transparent opacity={0} depthWrite={false}
            blending={THREE.AdditiveBlending} toneMapped={false}
          />
        </sprite>
      ))}
    </group>
  );
}

// ── THE THROWN FIRE — a Hurl, in flight ──────────────────────
// Its POSITION is the timer. Nothing else needed. It rides the
// same bezier as everything else, so a hurled ember curves in
// from the side its key names instead of flying down the road.
function Orb({ orbRef, posRef, foePos, foeState }) {
  const { camera } = useThree();
  const core = useRef();
  const tail = useRef([]);
  const jet = useRef([]);
  const tex = useMemo(() => billowTexture(), []);
  const a = useRef(new THREE.Vector3());
  const c = useRef(new THREE.Vector3());
  const b = useRef(new THREE.Vector3());
  const m = useRef(new THREE.Vector3());
  const v = useRef(new THREE.Vector3());
  const from = useRef(new THREE.Vector3());
  const armed = useRef(0);

  // The curve for whatever kind this is, solved into a/c/b. One
  // function, four projectiles, and a fifth costs a branch.
  const solve = (o, p, fp, k) => {
    if (o.kind === "comet") {
      // ── THE COMET ── it does not come off the creature. It comes
      // out of the SKY, which is the entire reason it is on the
      // level after the one that teaches you to read creatures.
      skyPoint(camera, p, o.angle, a.current);
      roseWorld(camera, p, o.angle, b.current);
      c.current.set(
        (a.current.x + b.current.x) * 0.5,
        a.current.y * 0.55,
        (a.current.z + b.current.z) * 0.5
      );
      return bez(a.current, c.current, b.current, k, v.current);
    }
    if (o.kind === "coin") {
      // ── THE RICOCHET ── caP'n's spec: two beziers, one bounce.
      // The first half runs foe → boards; the second boards →
      // guard. The angle it LEFT on is not the angle it ARRIVES
      // on, which is the whole lesson of the Mudlark and the
      // reason it can be taught with something funny.
      bouncePoint(camera, p, fp, o.angle, m.current);
      if (k < 0.46) {
        const kk = k / 0.46;
        c.current.set(
          (from.current.x + m.current.x) * 0.5,
          Math.max(from.current.y, 0.9) + 0.35,
          (from.current.z + m.current.z) * 0.5
        );
        return bez(from.current, c.current, m.current, kk, v.current);
      }
      const kk = (k - 0.46) / 0.54;
      roseWorld(camera, p, o.angle, b.current);
      bounceControl(camera, m.current, b.current, c.current);
      return bez(m.current, c.current, b.current, kk, v.current);
    }
    // orb / jet / anything else: the hook, as it always was
    a.current.copy(from.current);
    roseWorld(camera, p, o.angle, b.current);
    hookControl(camera, p, o.angle, c.current);
    return bez(a.current, c.current, b.current, k, v.current);
  };

  useFrame((state) => {
    const o = orbRef?.current;
    const cm = core.current;
    if (!cm) return;
    if (!o || !o.active) {
      cm.visible = false;
      armed.current = 0;
      tail.current.forEach((x) => { if (x) x.visible = false; });
      jet.current.forEach((x) => { if (x) x.visible = false; });
      return;
    }
    const p = posRef.current;
    const i = actorIndex(foeState);
    const fp = foePos.current[i] || { x: p.x + 1.2, y: 0, z: p.z };

    // freeze the launch point the instant it leaves the hand — the
    // foe keeps moving and the fire does not follow it
    if (armed.current !== o.t0) {
      armed.current = o.t0;
      from.current.set(fp.x, 0.86, fp.z);
    }

    const t = performance.now();
    let k = Math.max(0, Math.min(1, (t - o.t0) / Math.max(1, o.t1 - o.t0)));

    // ══════════════════════════════════════════════════════════
    //  THE DEFLECT — R-27, 2026-07-28
    // ══════════════════════════════════════════════════════════
    // "Parrying thrown fire currently makes the orb vanish and
    //  throws sparks. It should come off the Treasure and GO
    //  somewhere — ideally back into the thing that threw it on a
    //  PERFECT."
    //
    // It plays the same curve backwards. On a PERFECT it runs all
    // the way home; on a GOOD it is a short spinning stumble that
    // drops out of the air halfway. No new geometry, no new
    // solver — the stage had the bezier the whole time and simply
    // never ran it in reverse.
    let dead = false;
    if (o.back) {
      const age = (t - (o.backAt || t)) / (o.back >= 1 ? 420 : 260);
      const u = Math.max(0, Math.min(1, age));
      k = 1 - u * (o.back >= 1 ? 1 : 0.42);
      if (u >= 1) dead = true;
    }
    if (dead) {
      cm.visible = false;
      tail.current.forEach((x) => { if (x) x.visible = false; });
      jet.current.forEach((x) => { if (x) x.visible = false; });
      return;
    }

    solve(o, p, fp, k);
    cm.visible = true;
    cm.position.copy(v.current);

    // ── PER-KIND PRESENTATION ──
    // The flight is shared; the LOOK is not. A comet is bigger and
    // trails; a coin is small, hard and spins; a jet is not a
    // projectile at all, it is a line.
    const isCoin = o.kind === "coin";
    const isComet = o.kind === "comet";
    const isJet = o.kind === "jet";
    cm.scale.setScalar(
      isCoin ? 0.13 + k * 0.03
        : isComet ? 0.40 + k * 0.26
          : 0.30 + k * 0.16
    );
    if (cm.material) {
      cm.material.color.set(o.tell);
      cm.material.opacity = isJet ? 0.85 : 1;
      // a coin catches the moon rather than glowing — it flickers
      // as it turns, which is what a spinning disc does
      if (isCoin) cm.material.opacity = 0.55 + Math.abs(Math.sin(t * 0.028)) * 0.45;
    }

    // ── THE TRAIL ── a comet gets a long one, a coin gets none.
    const trailN = isComet ? 3 : isCoin ? 0 : 3;
    const spacing = isComet ? 0.085 : 0.055;
    tail.current.forEach((x, n) => {
      if (!x) return;
      if (n >= trailN) { x.visible = false; return; }
      const lag = Math.max(0, k - (n + 1) * spacing);
      x.visible = lag > 0;
      solve(o, p, fp, lag);
      x.position.copy(v.current);
      x.scale.setScalar((isComet ? 0.34 - n * 0.06 : 0.24 - n * 0.05) * (0.6 + lag));
      if (x.material) {
        x.material.color.set(o.tell);
        x.material.opacity = (isComet ? 0.55 : 0.42) - n * 0.11;
      }
    });

    // ══════════════════════════════════════════════════════════
    //  THE JET — a hold has no arrival point, it has a LINE
    // ══════════════════════════════════════════════════════════
    // Eight beads strung between the foe's mouth and the guard,
    // held open for the whole ride rather than travelling. The
    // player is not watching something come; they are watching
    // something CONTINUE, which is the only way a hold reads as a
    // hold and not as a very slow hit.
    jet.current.forEach((x, n) => {
      if (!x) return;
      if (!isJet) { x.visible = false; return; }
      const u = (n + 0.5) / jet.current.length;
      jetMouth(fp, a.current);
      roseWorld(camera, p, o.angle, b.current);
      hookControl(camera, p, o.angle, c.current);
      bez(a.current, c.current, b.current, Math.min(1, u * Math.max(k, 0.15) * 1.6), v.current);
      x.visible = true;
      x.position.copy(v.current);
      // it BREATHES down its length, so the stream has direction
      const pulse = 0.6 + Math.sin(t * 0.018 - u * 7) * 0.4;
      x.scale.setScalar((0.14 + u * 0.20) * (0.7 + pulse * 0.5));
      if (x.material) {
        x.material.color.set(o.tell);
        x.material.opacity = 0.30 + pulse * 0.3;
      }
    });
  });

  return (
    <group>
      <sprite ref={core} scale={0.3}>
        <spriteMaterial map={tex} transparent depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
      </sprite>
      {[0, 1, 2].map((i) => (
        <sprite key={i} ref={(el) => { tail.current[i] = el; }} scale={0.2}>
          <spriteMaterial map={tex} transparent opacity={0.3} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
        </sprite>
      ))}
      {/* the jet's beads — a fixed pool, mounted always, hidden
          when nothing is pouring. Never conditionally rendered:
          mounting eight sprites mid-fight is a frame nobody
          budgeted for. */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <sprite key={`j${i}`} ref={(el) => { jet.current[i] = el; }} scale={0.2} visible={false}>
          <spriteMaterial map={tex} transparent opacity={0.3} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
        </sprite>
      ))}
    </group>
  );
}

// ── SPARKS — what a parry looks like when it works ───────────
// A fixed pool of 12, replayed off sparkRef.at. Fixed count, no
// allocation, no mount/unmount. PERFECT throws bright and wide;
// a MISS throws almost nothing, which is its own feedback. They
// burst at the ROSE POINT, so a clean answer visibly happens
// where your finger said it would.
function Sparks({ sparkRef, posRef }) {
  const { camera } = useThree();
  const refs = useRef([]);
  const tex = useMemo(() => billowTexture(), []);
  const seenAt = useRef(0);
  const origin = useRef(new THREE.Vector3());
  const seeds = useMemo(() => {
    const R = mulberry32(5150);
    return Array.from({ length: 12 }, () => ({
      ax: (R() - 0.5) * 2, ay: R() * 1.4 + 0.2, az: (R() - 0.5) * 1.4,
      rate: 0.8 + R() * 0.7, size: 0.10 + R() * 0.13,
    }));
  }, []);

  useFrame(() => {
    const s = sparkRef?.current;
    if (!s) return;
    if (s.at !== seenAt.current) {
      seenAt.current = s.at;
      roseWorld(camera, posRef.current, s.angle, origin.current);
    }
    const t = performance.now();
    const age = (t - seenAt.current) / 620;
    const power = s.grade === "PERFECT" ? 1 : s.grade === "GOOD" ? 0.6 : s.grade === "HELD" ? 0.25 : 0.18;
    seeds.forEach((sd, i) => {
      const m = refs.current[i];
      if (!m) return;
      if (age > 1 || age < 0) { m.visible = false; return; }
      m.visible = true;
      const k = age * sd.rate;
      m.position.set(
        origin.current.x + sd.ax * k * 0.62 * power,
        origin.current.y + sd.ay * k * 0.5 * power - k * k * 0.35,
        origin.current.z + sd.az * k * 0.5 * power
      );
      m.scale.setScalar(sd.size * (1 - age * 0.6) * (0.6 + power));
      if (m.material) {
        m.material.opacity = Math.max(0, (1 - age) * power);
        m.material.color.set(
          s.grade === "PERFECT" ? "#ffe6c2" : s.grade === "MISS" ? "#f4726a" : s.tell || "#ffb058"
        );
      }
    });
  });

  return (
    <group>
      {seeds.map((_, i) => (
        <sprite key={i} ref={(el) => { refs.current[i] = el; }} visible={false}>
          <spriteMaterial map={tex} transparent opacity={0} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
        </sprite>
      ))}
    </group>
  );
}

// ── FRACTURE — what surviving a Calamity buys you ────────────
// A teal ring around a thing that has come apart and cannot
// currently object to anything you do to it.
function FractureRing({ fractureRef, foePos, foeState }) {
  const ring = useRef();
  useFrame((state) => {
    const r = ring.current;
    if (!r) return;
    const until = fractureRef?.current || 0;
    const t = performance.now();
    const on = t < until;
    const i = actorIndex(foeState);
    const fp = foePos.current[i];
    if (fp) r.position.set(fp.x, 0.08, fp.z);
    const want = on ? 0.55 : 0;
    if (r.material) r.material.opacity += (want - r.material.opacity) * 0.14;
    r.visible = r.material ? r.material.opacity > 0.01 : false;
    const pulse = 1 + Math.sin(state.clock.elapsedTime * 6) * 0.08;
    r.scale.setScalar(on ? pulse : 1);
  });
  return (
    <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[6, 0.08, 0]} visible={false}>
      <ringGeometry args={[0.42, 0.62, 28]} />
      <meshBasicMaterial color="#4ff0dc" transparent opacity={0} depthWrite={false} toneMapped={false} side={THREE.DoubleSide} />
    </mesh>
  );
}

// ════════════════════════════════════════════════════════════
// THE CHAIN KUNAI — Th'rakk's beat, drawn in the world
// ════════════════════════════════════════════════════════════
// caP'n's spec: "stand in front of you, throw it behind you and
// have it come from the side somewhere."
//
// Four movements, all off the ONE strike object the director
// already built, so the blade can never leave the hand on a
// different frame than the hand opens:
//
//   spawn → castAt    it draws back. The blade is still its hand's.
//   castAt → behindAt the throw. It flies PAST Perry, out of the
//                     frame's business entirely, and the chain
//                     pays out behind him going slack.
//   behindAt → yankAt the wait. This is the scary part: the only
//                     thing on screen is a chain running off the
//                     edge of the shot, and you do not yet know
//                     which side it is coming back on.
//   yankAt → impact   THE HAUL. The slack snaps out of the chain
//                     in a single frame — that flash IS the tell —
//                     and the blade whips around on the rolled
//                     angle and arrives.
//
// Everything is imperative off refs. The component never
// re-renders, allocates nothing per frame, and spends no lights:
// the blade's glow is emissive, the chain's is additive.
const CHAIN_N = 26;
const TRAIL_N = 5;

function ChainKunai({ strikeRef, posRef, foePos, foeState }) {
  const { camera } = useThree();
  const blade = useRef();
  const spin = useRef();
  const flash = useRef();
  const links = useRef([]);
  const trail = useRef([]);
  const tex = useMemo(() => billowTexture(), []);

  // scratch — allocated once, ever
  const V = useRef({
    hand: new THREE.Vector3(),
    behind: new THREE.Vector3(),
    rose: new THREE.Vector3(),
    ctrl: new THREE.Vector3(),
    at: new THREE.Vector3(),
    prev: new THREE.Vector3(),
    mid: new THREE.Vector3(),
    tmp: new THREE.Vector3(),
    frozen: new THREE.Vector3(),
    armed: 0,
  }).current;

  const hideAll = () => {
    if (blade.current) blade.current.visible = false;
    if (flash.current) flash.current.visible = false;
    for (let i = 0; i < links.current.length; i++) {
      const L = links.current[i];
      if (L) L.visible = false;
    }
    for (let i = 0; i < trail.current.length; i++) {
      const T = trail.current[i];
      if (T) T.visible = false;
    }
  };

  useFrame((state, dt) => {
    const st = strikeRef?.current;
    const b = blade.current;
    if (!b) return;
    if (!st || st.projectile !== "kunai" || !st.fr) { hideAll(); return; }

    const p = posRef.current;
    const i = actorIndex(foeState);
    const fp = foePos.current[i] || { x: p.x + 1.2, y: 0, z: p.z };
    const t = performance.now();

    // the hand it is thrown from. Th'rakk's throwing hand — the
    // one the Haul is shackled to — sits high on the body.
    handPoint(fp, p, V.hand, 1.15);

    // the two solved ends of the whole motion
    roseWorld(camera, p, st.angle, V.rose);

    // FREEZE the behind point the instant the blade leaves. The
    // warden keeps swaying afterwards and a blade tied to a
    // swaying shoulder would drift, which would quietly make the
    // "which side" read a lie.
    if (V.armed !== st.spawnAt) {
      V.armed = st.spawnAt;
      wrapBehind(camera, p, fp, V.frozen);
      V.prev.copy(V.hand);
    }
    V.behind.copy(V.frozen);

    let slack = 1;          // 1 = the chain is loose, 0 = dead taut
    let show = true;
    let speed = 0;

    if (t < st.castAt) {
      // still in its hand — the body is drawing it back
      show = false;
      V.at.copy(V.hand);
    } else if (t < st.behindAt) {
      // ── THE THROW ── past his ear and into the dark
      const u = (t - st.castAt) / Math.max(1, st.behindAt - st.castAt);
      V.ctrl.copy(V.hand).lerp(V.behind, 0.5);
      V.ctrl.y += 0.75;
      bez(V.hand, V.ctrl, V.behind, u, V.at);
      slack = 0.25 + u * 0.75;
      speed = 1;
    } else if (t < st.yankAt) {
      // ── THE WAIT ── it hangs back there, turning slowly
      const u = (t - st.behindAt) / Math.max(1, st.yankAt - st.behindAt);
      V.at.copy(V.behind);
      V.at.y += Math.sin(u * Math.PI * 2.4) * 0.06;
      slack = 1;
      speed = 0.15;
    } else {
      // ── THE HAUL ── the answer, arriving
      const u = Math.max(0, Math.min(1.25, (t - st.yankAt) / Math.max(1, st.impactAt - st.yankAt)));
      wrapControl(camera, p, st.angle, V.ctrl);
      bez(V.behind, V.ctrl, V.rose, Math.min(1, u), V.at);
      if (u > 1) {
        // past the beat: it keeps going and is reeled home
        V.at.lerp(V.hand, Math.min(1, (u - 1) * 3.4));
        show = u < 1.22;
      }
      slack = Math.max(0.04, 0.35 - u * 0.35);
      speed = 1;
    }

    b.visible = show;
    if (show) {
      b.position.copy(V.at);
      // point along travel. `lookAt` on the parent, spin on the
      // child, so the blade flies point-first AND rotates.
      V.tmp.copy(V.at).sub(V.prev);
      if (V.tmp.lengthSq() > 1e-7) {
        V.tmp.normalize().multiplyScalar(2).add(V.at);
        b.lookAt(V.tmp);
        b.rotateX(Math.PI / 2);          // the model's +Y is its point
      }
      if (spin.current) spin.current.rotation.y += dt * (5 + speed * 26);
    }
    V.prev.copy(V.at);

    // ── THE CHAIN — hand to blade, sagging or singing ──
    // The control point is the midpoint, dropped by the slack and
    // dragged backwards by the blade's own motion. That drag is
    // why the haul looks like a whip and not like a ruler.
    V.mid.copy(V.hand).lerp(V.at, 0.5);
    V.mid.y -= slack * 1.15;
    const n = links.current.length;
    for (let k = 0; k < n; k++) {
      const L = links.current[k];
      if (!L) continue;
      L.visible = show;
      if (!show) continue;
      const u = k / (n - 1);
      bez(V.hand, V.mid, V.at, u, V.tmp);
      L.position.copy(V.tmp);
      L.rotation.set(Math.PI / 2, u * 5.2, Math.sin(u * 6.1) * 0.6);
      const sc = 0.9 + Math.sin(u * Math.PI) * 0.15;
      L.scale.setScalar(sc);
    }

    // ── THE SNAP ── the frame the slack leaves the chain. This is
    // the single most important frame in the attack: it is the
    // moment the player is told the answer is now knowable.
    const f = flash.current;
    if (f) {
      const age = t - st.yankAt;
      const on = age >= -40 && age < 260;
      f.visible = on;
      if (on) {
        const k = 1 - Math.max(0, age) / 260;
        f.position.copy(V.hand);
        f.scale.setScalar(0.5 + (1 - k) * 2.6);
        if (f.material) f.material.opacity = k * 0.85;
      }
    }

    // ── the trail behind the blade ──
    for (let k = 0; k < trail.current.length; k++) {
      const T = trail.current[k];
      if (!T) continue;
      T.visible = show && speed > 0.4;
      if (!T.visible) continue;
      const lag = 0.055 * (k + 1);
      if (t >= st.yankAt) {
        const u = Math.max(0, Math.min(1, (t - st.yankAt) / Math.max(1, st.impactAt - st.yankAt)) - lag);
        bez(V.behind, V.ctrl, V.rose, u, V.tmp);
      } else {
        const u = Math.max(0, Math.min(1, (t - st.castAt) / Math.max(1, st.behindAt - st.castAt)) - lag);
        bez(V.hand, V.ctrl, V.behind, u, V.tmp);
      }
      T.position.copy(V.tmp);
      T.scale.setScalar(0.34 - k * 0.05);
      if (T.material) T.material.opacity = 0.4 - k * 0.07;
    }
  });

  return (
    <group>
      {/* the blade itself — the SAME model its hand was holding */}
      <group ref={blade} visible={false}>
        <group ref={spin}>
          <Kunai hot="#6fe6d4" scale={1.35} />
        </group>
        {/* the cold light it drags along with it */}
        <sprite scale={0.75}>
          <spriteMaterial map={tex} color="#6fe6d4" transparent opacity={0.42}
            blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </sprite>
      </group>

      <ChainLinks refs={links} count={CHAIN_N} tint="#6fe6d4" scale={1} />

      {Array.from({ length: TRAIL_N }, (_, k) => (
        <sprite key={k} ref={(el) => { trail.current[k] = el; }} visible={false}>
          <spriteMaterial map={tex} color="#6fe6d4" transparent opacity={0.3}
            blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </sprite>
      ))}

      <sprite ref={flash} visible={false}>
        <spriteMaterial map={tex} color="#c8fff4" transparent opacity={0}
          blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
      </sprite>
    </group>
  );
}

// ════════════════════════════════════════════════════════════
// THE FOE'S PIPS — a mini bar that dims when you answer
// ════════════════════════════════════════════════════════════
// caP'n, 2026-07-27: "he has a mini health bar we dim every time
// we parry an attack."
//
// It is honest about the Lyf Cycle, which matters more than it
// sounds. A pip is only SPENT by a real bite. But every graded
// answer makes the whole bar shiver and the frontmost pip flare —
// so contact always reads, even on the answers the Empire says do
// no damage. Feedback for the hand, truth for the numbers.
//
// Opt-in via `ENEMIES[x].bar`. A road full of health bars is a
// menu, not a road.
const PIP_MAX = 9;

let PIP_TEX = null;
function pipTexture() {
  if (PIP_TEX) return PIP_TEX;
  const c = document.createElement("canvas");
  c.width = 64; c.height = 128;
  const g = c.getContext("2d");
  // a tapered ember lozenge with a hot core and a hard rim
  const grad = g.createLinearGradient(0, 0, 0, 128);
  grad.addColorStop(0.0, "rgba(255,255,255,0)");
  grad.addColorStop(0.16, "rgba(255,255,255,0.95)");
  grad.addColorStop(0.5, "rgba(255,240,215,0.75)");
  grad.addColorStop(0.84, "rgba(255,255,255,0.95)");
  grad.addColorStop(1.0, "rgba(255,255,255,0)");
  g.fillStyle = grad;
  g.beginPath();
  g.moveTo(32, 4);
  g.quadraticCurveTo(58, 64, 32, 124);
  g.quadraticCurveTo(6, 64, 32, 4);
  g.closePath();
  g.fill();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  PIP_TEX = t;
  return t;
}

function FoePips({ vitalsRef, foePos, foeState }) {
  const { camera } = useThree();
  const pips = useRef([]);
  const rail = useRef();
  const tex = useMemo(() => pipTexture(), []);
  const bill = useMemo(() => billowTexture(), []);
  const at = useRef(new THREE.Vector3());

  useFrame((state) => {
    const v = vitalsRef?.current;
    const t = performance.now();
    const live = !!(v && v.show && v.max > 0 && (!v.hideAt || t < v.hideAt));

    if (rail.current) rail.current.visible = live;
    if (!live) {
      for (let i = 0; i < PIP_MAX; i++) {
        const m = pips.current[i];
        if (m) m.visible = false;
      }
      return;
    }

    const i0 = actorIndex(foeState);
    const fp = foePos.current[i0] || { x: 0, y: 0, z: 0 };
    const m4 = camera.matrixWorld.elements;

    // the bar rides above its head, on the camera's own right/up,
    // so it is level on the glass under every shot
    const shiver = Math.max(0, 1 - (t - (v.ringAt || 0)) / 260);
    const jitter = shiver * 0.02 * Math.sin(t * 0.09);
    at.current.set(fp.x, 2.28 + jitter, fp.z);

    if (rail.current) {
      rail.current.position.copy(at.current);
      const w = 0.16 * v.max + 0.16;
      rail.current.scale.set(w, 0.16, 1);
      if (rail.current.material) rail.current.material.opacity = 0.16 + shiver * 0.24;
    }

    const span = 0.155;
    const left = -((v.max - 1) * span) / 2;
    for (let i = 0; i < PIP_MAX; i++) {
      const m = pips.current[i];
      if (!m) continue;
      if (i >= v.max) { m.visible = false; continue; }
      m.visible = true;
      const off = left + i * span;
      m.position.set(
        at.current.x + m4[0] * off,
        at.current.y + m4[1] * off,
        at.current.z + m4[2] * off
      );

      const lit = i < v.anima;
      // the pip that JUST went out gets the whole moment
      const dying = i === v.anima && (t - (v.hitAt || 0)) < 420;
      const dieK = dying ? 1 - (t - v.hitAt) / 420 : 0;
      // the frontmost survivor flares on any contact
      const front = lit && i === v.anima - 1;
      const flare = front ? shiver : 0;

      const sc = 0.135 * (1 + flare * 0.5 + dieK * 1.1);
      m.scale.set(sc * 0.62, sc, 1);
      const mat = m.material;
      if (mat) {
        if (dying) {
          mat.color.set("#ffffff");
          mat.opacity = dieK;
        } else if (lit) {
          mat.color.set(flare > 0.02 ? "#ffffff" : "#6fe6d4");
          mat.opacity = 0.86 + flare * 0.14;
        } else {
          mat.color.set("#3a4a4c");
          mat.opacity = 0.28;
        }
      }
    }
  });

  return (
    <group>
      <sprite ref={rail} visible={false}>
        <spriteMaterial map={bill} color="#0b1a1c" transparent opacity={0.2}
          depthWrite={false} toneMapped={false} />
      </sprite>
      {Array.from({ length: PIP_MAX }, (_, i) => (
        <sprite key={i} ref={(el) => { pips.current[i] = el; }} visible={false}>
          <spriteMaterial map={tex} color="#6fe6d4" transparent opacity={0}
            blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </sprite>
      ))}
    </group>
  );
}

// ── THE LIGHT RIG — fixed count, one gain knob ───────────────
// ── THE LIGHT RIG — a FIXED budget of seven, in every biome ──
// caP'n's law, unchanged since the Cinders Pass: ambient + hemi +
// moon + one "hearth" + two foe lights + Perry's carried ember
// (inside PerryBody) = 7, always mounted, never conditionally
// rendered. A changing scene light count recompiles every shader
// in the scene, mid-fight.
//
// The Drowned Mile therefore does NOT get a light of its own. It
// REPOINTS the one the Cinders Road was already using: Reedbrook's
// pyre becomes the drowned chapel's rose window, which sits ahead
// of Perry instead of behind him and burns violet instead of
// orange, and does not flicker, because nothing in that chapel is
// on fire. Same light. Opposite meaning. Zero cost.
function Rig({ posRef, foeTint, biome = "cinders" }) {
  const fen = biome === "fen";
  const hearth = useRef();
  const foeA = useRef();
  const foeB = useRef();
  const moon = useRef();
  const moonAt = useRef();
  const g = LIGHT.GAIN;

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const x = posRef?.current?.x ?? 0;
    if (hearth.current) {
      if (fen) {
        // ── THE CHAPEL ── it does not flicker. It is not fire; it
        // is whatever is still in the rose window, and it has been
        // steady for a very long time. It sits at the chapel and
        // stays there, so walking the mile is literally walking
        // toward the only light in the level.
        hearth.current.intensity = 5.2 * g;
        hearth.current.position.set(41, 2.9, -2.2);
        hearth.current.color.set("#a06ac0");
        hearth.current.distance = 34;
      } else {
        // the village behind him, still burning — imperative flicker
        hearth.current.intensity = (7 + Math.sin(t * 4.1) * 1.6 + Math.sin(t * 11.7) * 0.7) * g;
        hearth.current.position.set(Math.min(x - 6, 8), 3.4, -2);
      }
    }
    // ── THE MOON TRAVELS WITH HIM ──
    // Added with the Cinders Pass. The shadow camera is bounded to
    // ±16 for sharpness, which meant that past x≈16 the road
    // simply stopped casting shadows — half of Act I was lit flat
    // and nobody noticed because there was nothing out there to
    // cast one. There is now. The light and its target ride his x,
    // so the same tight, crisp shadow box follows him to Hollowmere.
    if (moon.current && moonAt.current) {
      // In the fen the moon comes in LOWER and from the far side,
      // because a low raking light is the entire reason still water
      // reads as water. Same light, same shadow box, one vector.
      if (fen) moon.current.position.set(x - 20, 11, -20);
      else moon.current.position.set(x - 14, 22, -12);
      moonAt.current.position.set(x, 0, 0);
      moonAt.current.updateMatrixWorld();
      moon.current.target = moonAt.current;
    }
  });

  return (
    <group>
      <ambientLight intensity={(fen ? 0.13 : 0.16) * g} color={fen ? "#2a3a44" : "#4a3a52"} />
      <hemisphereLight args={[fen ? "#22343a" : "#3a2438", fen ? "#060e10" : "#140c08", (fen ? 0.30 : 0.34) * g]} />
      {/* the moon, only real shadow caster, tightly bounded */}
      <object3D ref={moonAt} />
      <directionalLight
        ref={moon}
        position={[-14, 22, -12]}
        intensity={(fen ? 0.62 : 0.46) * g}
        color={fen ? "#b6cfe6" : "#8fa6d8"}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-near={1}
        shadow-camera-far={60}
        shadow-camera-left={-16}
        shadow-camera-right={16}
        shadow-camera-top={16}
        shadow-camera-bottom={-16}
        shadow-bias={-0.0012}
      />
      {/* THE ONE PLACED LIGHT. Reedbrook burning behind him on the
          road; the chapel's rose window ahead of him in the fen.
          Never two of them, never zero. */}
      <pointLight ref={hearth} intensity={7 * g} distance={46} decay={1.15} color="#ff6a1e" castShadow={false} />
      {/* two PERMANENT foe lights — see the note in EnemyBody */}
      <pointLight ref={foeA} intensity={0} distance={6} decay={1.6} color="#ff8a3c" castShadow={false} />
      <pointLight ref={foeB} intensity={0} distance={6} decay={1.6} color="#ff8a3c" castShadow={false} />
      <FoeLightDriver refs={[foeA, foeB]} tint={foeTint} />
    </group>
  );
}

// Drives the two permanent foe lights from the director's refs.
function FoeLightDriver({ refs, tint = "#ff8a3c" }) {
  const ctx = useStageRefs();
  useFrame(() => {
    for (let i = 0; i < 2; i++) {
      const l = refs[i].current;
      if (!l) continue;
      // Th'rakk burns teal and the road has to agree with him. Two
      // lights, permanently mounted, recoloured — never remounted.
      l.color.set(tint);
      const st = ctx.foeState?.current?.[i];
      const p = ctx.foePos?.current?.[i];
      const on = st && st.phase !== "hidden" && st.phase !== "retreat";
      if (p) l.position.set(p.x, 0.85, p.z);
      const want = on ? 2.2 * LIGHT.GAIN : 0;
      l.intensity += (want - l.intensity) * 0.12;   // fade, never mount/unmount
    }
  });
  return null;
}

// ── THE CAMERA — solved marks, cuts, dollies on rails ────────
function ShotCamera({ shotRef, posRef, facingRef, shakeRef }) {
  const { camera } = useThree();
  const aim = useRef(new THREE.Vector3(4, 1, 0));
  const cur = useRef(new THREE.Vector3(-6, 4.2, 9.5));
  const lastMode = useRef("");
  const tmp = useRef(new THREE.Vector3());

  useFrame((state, dt) => {
    const s = shotRef.current;
    const p = posRef.current;
    if (!s || !p) return;

    let wantPos = tmp.current;
    let wantAim = aim.current;

    if (s.mode === "fixed") {
      wantPos.set(s.pos[0], s.pos[1], s.pos[2]);
      wantAim.set(s.aim[0], s.aim[1], s.aim[2]);
    } else if (s.mode === "dolly") {
      wantPos.set(p.x + s.dx, s.y, s.z);
      wantAim.set(p.x + (s.aimDx || 0), (s.aimDy || 1), p.z);
    } else {
      // first person — his eyes, his facing
      const yaw = (facingRef.current || 0) + (s.yaw || 0);
      wantPos.set(p.x, BODY_EYE, p.z);
      wantAim.set(p.x + Math.cos(yaw) * 3, BODY_EYE - 0.05, p.z + Math.sin(yaw) * 3);
    }

    // A CUT: new shot mode/mark → snap. Within a shot → glide.
    const modeKey = `${s.mode}:${s.pos ? s.pos.join(",") : s.dx}`;
    if (modeKey !== lastMode.current) {
      lastMode.current = modeKey;
      cur.current.copy(wantPos);
    } else {
      cur.current.lerp(wantPos, Math.min(1, dt * 6));
    }

    // shake — imperative, decays in the director. The jitter is
    // deterministic noise off the clock rather than Math.random,
    // so a recorded run shakes identically twice.
    const sh = shakeRef.current || 0;
    const ct = state.clock.elapsedTime;
    const jx = sh ? (Math.sin(ct * 137.1) + Math.sin(ct * 51.7)) * 0.5 * sh * 0.28 : 0;
    const jy = sh ? (Math.sin(ct * 91.3) + Math.sin(ct * 173.9)) * 0.5 * sh * 0.24 : 0;

    camera.position.set(cur.current.x + jx, cur.current.y + jy, cur.current.z);
    camera.lookAt(wantAim);
    const fov = s.fov || 42;
    if (Math.abs(camera.fov - fov) > 0.01) {
      camera.fov += (fov - camera.fov) * Math.min(1, dt * 5);
      camera.updateProjectionMatrix();
    }
  });
  return null;
}

// ── THE STAGE ────────────────────────────────────────────────
export default function CinematicStage({ director, species }) {
  const wrap = useRef();
  const {
    pos, facing, speed, shake, foePos, foeState, shotRef,
    strikeRef, guardRef, orbRef, sparkRef, fractureRef, foeVitals, sightRef,
    treasure, finish,
  } = director;

  // The enemy TYPE is discrete state (new geometry, new textures), so
  // it arrives through the HUD, not through a ref. Everything that
  // moves per-frame arrives through refs. That split is the whole law.
  // ── STICKY FOE IDENTITY — fixed 2026-07-29 ────────────────
  // caP'n: "after I parried and the fight was over he turned red
  // and had a scythe."
  //
  // He did. `hud.foe` goes null the instant an encounter clears,
  // and this line fell straight back to `burning_husk` — which is
  // ember-red and carries a tool. So every foe in the game spent
  // its death frames MORPHING INTO A DIFFERENT ENEMY on the way
  // out. It was least visible on the road, where most things are
  // husks anyway; in the fen, a grey hound turning into a red man
  // with a scythe is impossible to miss.
  //
  // Sticky state usually lies (that is the house rule) — but the
  // thing being remembered here is IDENTITY, which genuinely does
  // not change while a body is on screen. The lie was the
  // fallback, not the memory. It only ever resets to the default
  // before the first foe of a run has been named.
  const lastFoe = useRef("burning_husk");
  if (director.hud?.foe?.id) lastFoe.current = director.hud.foe.id;
  const enemyId = lastFoe.current;
  const foeTint = (ENEMIES[enemyId] || ENEMIES.burning_husk).tint;
  // ── WHICH WORLD ── discrete, like the enemy type: new geometry,
  // new textures, a new sky. It arrives on the director's act row,
  // not through a ref, because nothing about it changes per frame.
  const biome = director.act?.biome || "cinders";
  const fen = biome === "fen";

  // NOTE ON SIZING: the Canvas measures its PARENT element, which is
  // this absolutely-positioned wrapper — never window.innerWidth. A
  // canvas sized to the window is the classic IE layout bug.
  const ctxValue = useMemo(() => ({ foePos, foeState }), [foePos, foeState]);

  return (
    <div ref={wrap} style={{ position: "absolute", inset: 0, background: "#050302" }}>
      <Canvas
        shadows
        dpr={[1, 1.5]}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        camera={{ position: [-6, 4.2, 9.5], fov: 42, near: 0.1, far: 400 }}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = 1.12;
          gl.outputColorSpace = THREE.SRGBColorSpace;
        }}
      >
        <StageRefs.Provider value={ctxValue}>
          {/* pushed from 66 to 74 with the Cinders Pass: there is
              mid-distance worth seeing now, and the ridges past it
              are fog-exempt, so nothing gets lost either way */}
          {/* Fog is the biome's, and it is one of the two biggest
              levers either world has. The road: 12 → 74, hot brown,
              because there is mid-distance worth seeing and the
              ridges past it are fog-exempt. The fen: 10 → 62, cold
              and close, because standing water eats distance and
              because a horizon four feet away in every direction is
              the whole feeling of the level. */}
          <fog attach="fog" args={fen ? FEN_FOG : ["#1a0e0a", 12, 74]} />
          <Rig posRef={pos} foeTint={foeTint} biome={biome} />

          {/* ══════════════════════════════════════════════════
              THE WORLD — one of them, never both
              ══════════════════════════════════════════════════
              Everything BELOW this block is level-agnostic: the
              bodies, the fight layers, the guard rose, the camera.
              That split is the whole reason a third biome is a
              file and an import rather than a session. */}
          {fen ? (
            <FenWorld posRef={pos} />
          ) : (
            <>
              <SkyDome />
              <Nightlights />
              <Ridges />
              <Terrain />
              <Road />
              <Scatter />
              <Fenceline />
              <Ruins />
              <Reedbrook />
              <MistBands />
              <Crow x={15} z={-3.4} />
              <LanternGate />
              <AshMotes count={520} />
              <Embers count={240} />
            </>
          )}

          {/* ── THE CROW ── she is on a fencepost at Reedbrook and a
              mooring post in the fen, and both times she was there
              BEFORE him. She is not following the tale; the tale is
              going where she already is. Same component, second
              post, and the walk line does the arithmetic out loud. */}
          {fen && <Crow x={20.6} z={-2.9} perch={1.05} />}

          <PerryBody
            species={species}
            posRef={pos}
            facingRef={facing}
            speedRef={speed}
            treasure={treasure}
            finish={finish}
            guardRef={guardRef}
          />
          <EnemyBody enemyId={enemyId} posArr={foePos} stateArr={foeState} index={0} perryRef={pos} />
          <EnemyBody enemyId={enemyId} posArr={foePos} stateArr={foeState} index={1} perryRef={pos} />

          {/* ── the fight, told entirely in the world ── */}
          <LaneDriver posRef={pos} foePos={foePos} foeState={foeState} strikeRef={strikeRef} />
          <StrikeArc strikeRef={strikeRef} posRef={pos} foePos={foePos} foeState={foeState} half={0} />
          <StrikeArc strikeRef={strikeRef} posRef={pos} foePos={foePos} foeState={foeState} half={1} />
          <Orb orbRef={orbRef} posRef={pos} foePos={foePos} foeState={foeState} />
          <Sparks sparkRef={sparkRef} posRef={pos} />
          <HintRose posRef={pos} strikeRef={strikeRef} shotRef={shotRef} sightRef={sightRef} />
          <FractureRing fractureRef={fractureRef} foePos={foePos} foeState={foeState} />
          <ChainKunai strikeRef={strikeRef} posRef={pos} foePos={foePos} foeState={foeState} />
          <FoePips vitalsRef={foeVitals} foePos={foePos} foeState={foeState} />

          <ShotCamera shotRef={shotRef} posRef={pos} facingRef={facing} shakeRef={shake} />
        </StageRefs.Provider>
      </Canvas>

      {/* ════════════════════════════════════════════════════
          THE GRADE — vignette + grain, in the DOM
          ════════════════════════════════════════════════════
          Every frame you have ever called cinematic had both of
          these and neither costs a single GPU cycle here. The
          grain is an inline SVG turbulence filter, so there is no
          image to load, no keyframe to inject, and nothing to
          404. `pointerEvents: none` on both, always — QTEOverlay
          is a sibling rendered after this and must keep the
          keyboard and the pointer to itself. */}
      <div style={VIGNETTE} />
      <div style={GRAIN} />
    </div>
  );
}

const VIGNETTE = {
  position: "absolute", inset: 0, pointerEvents: "none", zIndex: 2,
  background:
    "radial-gradient(ellipse 78% 68% at 50% 46%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.34) 74%, rgba(4,2,1,0.70) 100%)",
};

const GRAIN = {
  position: "absolute", inset: 0, pointerEvents: "none", zIndex: 3,
  opacity: 0.055, mixBlendMode: "overlay",
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'>" +
    "<filter id='g'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/></filter>" +
    "<rect width='160' height='160' filter='url(%23g)' opacity='0.55'/></svg>\")",
  backgroundRepeat: "repeat",
};
